import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Plus, Package, X, ChevronLeft, ChevronRight, Edit, Trash2, Mail, Calendar, Building2, User, Eye, Printer, Truck, ClipboardList, ArrowRight, MapPin, Users, CheckCircle2, Clock, ExternalLink, Camera, Image, Upload, ZoomIn, Search } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';
import M45NavPills from '../components/M45NavPills';
import ProductLookup from '../components/ProductLookup';
import { useBranch } from '../context/BranchContext';

const API = process.env.REACT_APP_BACKEND_URL;
const enconOrange = '#ee7203';

// Branch addresses - maps branch codes to their details
const BRANCH_ADDRESSES = {
  'C30': {
    name: 'Encon Insulation Birmingham',
    address1: '21 Gavin Way',
    address2: 'Nexus Point',
    city: 'Birmingham',
    postcode: 'B6 7AF',
    phone: '0121 3560606',
    code: 'C30'
  },
  'E70': {
    name: 'Nevill Long West Bromwich',
    address1: 'Unit 1-5 Hill Top Industrial Estate',
    address2: '',
    city: 'West Bromwich',
    postcode: 'B70 0TX',
    phone: '0121 5061710',
    code: 'E70'
  }
};

// Helper function to get current branch details
const getOurBranch = (branchId) => {
  return BRANCH_ADDRESSES[branchId] || BRANCH_ADDRESSES['C30'];
};

// Branch data from spreadsheet
const BRANCHES = [
  { id: 'birmingham', name: 'Encon Birmingham', contact: '', address1: '21 Gavin Way', address2: 'Perry Barr', city: 'Birmingham', postcode: 'B6 7AF', phone: '0121 356 0606', email: 'enconbirmingham@encon.co.uk', branchCode: 'C30' },
  { id: 'nottingham', name: 'Encon Nottingham', contact: 'Claire', address1: '4 Martin Close', address2: 'Bulwell', city: 'Nottingham', postcode: 'NG6 8UW', phone: '0115 9780040', branchCode: null },
  { id: 'west-bromwich', name: 'Nevill Long West Brom', contact: 'Neil', address1: 'Unit 1-5 Hill Top Industrial Estate', address2: '', city: 'West Bromwich', postcode: 'B70 0TX', phone: '0121 5061710', branchCode: 'E70' },
  { id: 'northampton', name: 'Encon Northampton', contact: 'Laura', address1: 'Unit 1, Burryport Road', address2: 'Brackmills Ind Est', city: 'Northampton', postcode: 'NN4 7BB', phone: '01604 667430', branchCode: null },
  { id: 'thetford', name: 'Nevill Long Thetford', contact: '', address1: '10 Burrell Way Trade Park', address2: 'Thetford', city: 'Norfolk', postcode: 'IP24 3RW', phone: '01842 777711', branchCode: null },
  { id: 'newmarket', name: 'Encon Newmarket', contact: 'Sam', address1: 'Unit 13, Willie Snaithe Road', address2: 'Studlands Park Ind Est', city: 'Newmarket', postcode: 'CB8 7AU', phone: '01638 667208', branchCode: null },
  { id: 'leeds', name: 'Encon Leeds', contact: 'Paul', address1: 'Unit 3, Benyon Park Way', address2: 'Off Low Fields Road', city: 'Leeds', postcode: 'LS12 6DP', phone: '0113 2207000', branchCode: null },
  { id: 'chorley', name: 'Encon North West', contact: 'Shaun', address1: 'Revolution Park', address2: 'Buckshaw Avenue', city: 'Chorley', postcode: 'PR7 7DW', phone: '01257 248220', branchCode: null },
  { id: 'newcastle', name: 'Encon Newcastle', contact: '', address1: 'Unit E2, High Flatworth', address2: 'Tyne Tunnel Trade Estate', city: 'Newcastle', postcode: 'NE29 7UZ', phone: '0191 2931090', branchCode: null },
  { id: 'washington', name: 'Washington Technical', contact: '', address1: '2 Faraday Close', address2: 'Pattison North Ind Est', city: 'Washington', postcode: 'NE38 8QJ', phone: '0191 4190066', branchCode: null },
  { id: 'dundee', name: 'Encon Dundee', contact: '', address1: 'Block 9, Myrekirk Road', address2: 'Wester Gourdie', city: 'Dundee', postcode: 'DD2 4SH', phone: '01382 610040', branchCode: null },
  { id: 'glasgow', name: 'Encon Glasgow', contact: 'Pauline', address1: '80 Cambuslang Road', address2: 'Cambuslang Investment Park', city: 'Glasgow', postcode: 'G32 8NB', phone: '0141 641 0011', branchCode: null },
  { id: 'plymouth', name: 'Encon Plymouth', contact: '', address1: 'Unit 4, Langage One', address2: 'Western Wood Way, Langage Science Park', city: 'Plymouth', postcode: 'PL7 5BE', phone: '01752 333720', branchCode: null },
  { id: 'fareham', name: 'Encon Fareham', contact: '', address1: 'Unit 3, Fareham Trade Park', address2: 'Lederle Lane', city: 'Gosport', postcode: 'PO13 0FE', phone: '01329 230555', branchCode: null },
  { id: 'cardiff', name: 'Encon Cardiff', contact: 'Elisha Symons', address1: 'Unit 4, Triden Park', address2: 'Ocean Way', city: 'Cardiff', postcode: 'CF24 5EP', phone: '02920 895040', branchCode: null },
  { id: 'bristol', name: 'Encon Bristol', contact: 'Thomas Turner', address1: 'Century Park', address2: 'Chittening Ind Estate', city: 'Avonmouth, Bristol', postcode: 'BS11 0YB', phone: '0117 9802100', branchCode: null },
  { id: 'witney', name: 'Encon Witney', contact: '', address1: 'Unit 5, Stanton Harcourt Ind Est', address2: 'Stanton Harcourt', city: 'Witney', postcode: 'OX29 5UX', phone: '01865 734500', branchCode: null },
  { id: 'oxford-technical', name: 'Oxford Technical', contact: 'Frankie', address1: 'Unit 8 Hikers Way, Crendon Ind Est', address2: 'Long Crendon', city: 'Buckinghamshire', postcode: 'HP18 9RW', phone: '01844 204510', branchCode: null },
  { id: 'east-london', name: 'Encon East London', contact: 'D53 Transfers', address1: 'Binary Park, Choats Road', address2: 'Dagenham', city: 'East London', postcode: 'RM9 6RJ', phone: '0208 5268440', branchCode: null },
  { id: 'maidstone', name: 'Encon Maidstone', contact: '', address1: 'Units F1 & F2, St Michaels Close', address2: 'Forstal Ind Est', city: 'Aylesford', postcode: 'ME20 7BU', phone: '01622 713400', branchCode: null },
  { id: 'west-london', name: 'Encon West London', contact: '', address1: 'Units 6-8', address2: 'The Griffin Centre, Staines Road', city: 'Feltham', postcode: 'TW14 0HS', phone: '0208 8311520', branchCode: null }
];

// ─── Branch search (shared by every branch picker on this page) ───────────────
// Matches name, nickname (e.g. "chorley"), city, postcode and branch code. Every word
// typed must match somewhere, in any order. Postcodes ignore spaces, so "b70 0tx",
// "b700tx" and "B70" all find West Bromwich.
const filterBranches = (branches, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return branches;
  const words = q.split(/\s+/).filter(Boolean);
  const compactQuery = q.replace(/\s+/g, '');
  return (branches || []).filter((b) => {
    const haystack = [b.name, b.nickname, b.city, b.branchCode, b.postcode, b.id]
      .filter(Boolean).join(' ').toLowerCase();
    const compactPostcode = String(b.postcode || '').replace(/\s+/g, '').toLowerCase();
    if (compactPostcode && compactQuery.length >= 2 && compactPostcode.startsWith(compactQuery)) return true;
    return words.every((w) => haystack.includes(w));
  });
};

const BranchSearchInput = ({ value, onChange, matches, onPick, testId }) => (
  <div className="relative mb-3">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        // Enter picks the branch when the search has narrowed to exactly one.
        if (e.key === 'Enter' && matches && matches.length === 1 && onPick) {
          e.preventDefault();
          onPick(matches[0]);
        }
      }}
      placeholder="Search branch — name, nickname (e.g. Chorley), postcode or branch code"
      className="pl-9 pr-9 rounded-xl"
      data-testid={testId}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        aria-label="Clear branch search"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

// ─── Recent Orders slide-out (Create Transfer modal) ──────────────────────────
const RECENT_ORDERS_PANEL_WIDTH = 380;

const fmtOrderDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); } catch (_) { return ''; }
};

const isCarriageLine = (line) => /carriage/i.test(String((line && (line.description || line.product_name)) || ''));

// Pure: puts one sales-order line onto the transfer. Fills the first blank line if there is
// one, otherwise appends. A product code already on the transfer is never added twice.
const addOrderLineToItems = (items, line) => {
  const code = String((line && (line.product_code || line.code)) || '').trim().toUpperCase();
  const description = (line && (line.description || line.product_name)) || '';
  if (!code && !description) return { items, added: false, reason: 'empty' };
  if (code && items.some((i) => String(i.product_code || '').toUpperCase() === code)) {
    return { items, added: false, reason: 'duplicate', code };
  }
  const quantity = parseFloat(line && (line.qty !== undefined && line.qty !== null ? line.qty : line.quantity)) || 1;
  const fresh = { product_code: code, description, quantity, stock_unit: 'EACH', weight: 0 };
  const blankIdx = items.findIndex((i) => !i.product_code && !i.description);
  if (blankIdx >= 0) {
    const next = items.slice();
    next[blankIdx] = { ...next[blankIdx], ...fresh };
    return { items: next, added: true, code };
  }
  return { items: [...items, { id: Date.now() + Math.random(), ...fresh }], added: true, code };
};

/**
 * Slide-out beside the Create Transfer modal listing recent APPROVED SALES ORDERS -- the
 * parsed M4 order emails (email_type="order", never quotes; the same source as B29's
 * "Build from Recent Orders"). Click an order to open its product lines, click a line to
 * add it to the transfer. Rendered INSIDE the dialog content so the dialog's focus and
 * pointer handling treat it as part of the modal.
 */
const RecentOrdersPanel = ({ onClose, onAddLine, existingCodes }) => {
  const panelRef = useRef(null);
  const [entered, setEntered] = useState(false);
  const [outside, setOutside] = useState(true);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Sit just outside the modal's right edge; if the screen is too narrow for that,
  // overlay the modal's right-hand side instead so it is never pushed off-screen.
  useLayoutEffect(() => {
    const measure = () => {
      const parent = panelRef.current && panelRef.current.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      setOutside(window.innerWidth - rect.right >= RECENT_ORDERS_PANEL_WIDTH + 24);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`${API}/api/automations/emails`, { params: { email_type: 'order', limit: 60 } });
        if (!cancelled) setOrders((res.data && res.data.emails) || []);
      } catch (e) {
        if (!cancelled) setError('Could not load recent orders');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const q = search.trim().toLowerCase();
  const visible = !q ? orders : orders.filter((o) => {
    const hay = [o.order_number, o.customer_name, o.invoice_customer_name, o.reference, o.delivery_postcode]
      .filter(Boolean).join(' ').toLowerCase();
    if (hay.includes(q)) return true;
    return (o.product_lines || []).some((l) => `${l.product_code || ''} ${l.description || ''}`.toLowerCase().includes(q));
  });
  const selected = orders.find((o) => o.id === selectedId) || null;

  const placement = outside
    ? { left: 'calc(100% + 12px)', top: 0, bottom: 0, width: RECENT_ORDERS_PANEL_WIDTH }
    : { right: 12, top: 12, bottom: 12, width: `min(${RECENT_ORDERS_PANEL_WIDTH}px, calc(100% - 24px))` };

  return (
    <div
      ref={panelRef}
      className="absolute z-10 flex flex-col bg-white shadow-2xl overflow-hidden"
      style={{
        ...placement,
        borderRadius: '24px',
        border: '3px solid transparent',
        backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        transform: entered ? 'translateX(0)' : (outside ? 'translateX(-28px)' : 'translateX(28px)'),
        opacity: entered ? 1 : 0,
        transition: 'transform 220ms ease-out, opacity 220ms ease-out',
      }}
      data-testid="recent-orders-panel"
    >
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b border-orange-100">
        <div className="min-w-0">
          <p className="font-bold text-base" style={{ color: enconOrange }}>Recent Orders</p>
          <p className="text-[11px] text-gray-500">Approved sales orders — not quotes</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100 flex-shrink-0"
          data-testid="recent-orders-close"
          aria-label="Close recent orders"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {selected ? (
        <>
          <div className="flex-shrink-0 px-4 py-2 border-b border-gray-100">
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800 mb-1"
              data-testid="recent-orders-back"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> All orders
            </button>
            <p className="font-mono font-bold text-sm text-gray-900">#{selected.order_number || '—'}</p>
            <p className="text-xs text-gray-600 truncate">{selected.customer_name || selected.invoice_customer_name || 'Unknown customer'}</p>
          </div>
          <p className="flex-shrink-0 px-4 pt-2 text-[11px] text-gray-500">Click a product to add it to the transfer</p>
          <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1.5">
            {(selected.product_lines || []).length === 0 && (
              <p className="text-center text-sm text-gray-400 py-8">This order has no product lines.</p>
            )}
            {(selected.product_lines || []).map((line, idx) => {
              const code = String(line.product_code || '').trim().toUpperCase();
              const carriage = isCarriageLine(line);
              const onTransfer = !!code && existingCodes.has(code);
              return (
                <button
                  key={`${selected.id}-${idx}`}
                  type="button"
                  disabled={carriage || onTransfer}
                  onClick={() => onAddLine(line)}
                  className={`w-full text-left flex items-center gap-2 rounded-xl border px-3 py-2 transition-all ${
                    carriage ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                      : onTransfer ? 'border-green-300 bg-green-50 cursor-default'
                      : 'border-gray-200 bg-white hover:border-orange-400 hover:bg-orange-50'
                  }`}
                  data-testid={`recent-orders-line-${idx}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-mono font-bold text-xs text-gray-900">{code || '—'}</p>
                    <p className="text-[11px] text-gray-500 truncate">{line.description || line.product_name || ''}</p>
                  </div>
                  <span className="text-xs font-semibold text-gray-600 flex-shrink-0">×{line.qty !== undefined && line.qty !== null ? line.qty : (line.quantity || 1)}</span>
                  {carriage ? (
                    <span className="text-[10px] font-semibold text-gray-400 flex-shrink-0">Carriage</span>
                  ) : onTransfer ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <Plus className="h-4 w-4 flex-shrink-0" style={{ color: enconOrange }} />
                  )}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="flex-shrink-0 px-4 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search order no, customer, postcode, product…"
                className="pl-8 h-9 text-xs rounded-lg"
                data-testid="recent-orders-search"
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 space-y-1.5">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-12 text-gray-400 text-sm">
                <div className="h-5 w-5 rounded-full border-2 border-orange-300 border-t-transparent animate-spin" />
                Loading orders…
              </div>
            ) : error ? (
              <p className="text-center text-sm text-red-600 py-10">{error}</p>
            ) : visible.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-10">{q ? `No orders match "${search.trim()}"` : 'No recent orders found.'}</p>
            ) : (
              visible.map((o) => {
                const lineCount = (o.product_lines || []).length;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setSelectedId(o.id)}
                    className="w-full text-left rounded-xl border border-gray-200 bg-white px-3 py-2 hover:border-orange-400 hover:bg-orange-50 transition-all"
                    data-testid={`recent-order-${o.order_number}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono font-bold text-sm text-gray-900">#{o.order_number || '—'}</span>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">{fmtOrderDate(o.received_at)}</span>
                    </div>
                    <p className="text-xs text-gray-700 truncate">{o.customer_name || o.invoice_customer_name || 'Unknown customer'}</p>
                    <p className="text-[10px] text-gray-400">
                      {[o.delivery_postcode, `${lineCount} line${lineCount === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
                    </p>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

const StockTransfer = ({ user }) => {
  const navigate = useNavigate();
  const { transferId } = useParams();
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const stActivityIdRef = useRef(null);
  
  // Branch context for data isolation
  const { getEffectiveBranchId } = useBranch();
  const effectiveBranchId = getEffectiveBranchId();
  
  // State
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBranchHistoryModal, setShowBranchHistoryModal] = useState(false);
  const [branchSearch, setBranchSearch] = useState('');   // Create Transfer modal (single + trunking pickers)
  const [browseSearch, setBrowseSearch] = useState('');   // "Browse by Branch" on the main page
  const [showRecentOrders, setShowRecentOrders] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedHistoryBranch, setSelectedHistoryBranch] = useState(null);
  const [historyMonth, setHistoryMonth] = useState(new Date().getMonth());
  const [historyYear, setHistoryYear] = useState(new Date().getFullYear());
  const [branchTransfers, setBranchTransfers] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Create transfer state
  const [requestedBy, setRequestedBy] = useState('');
  const [transferItems, setTransferItems] = useState([{ id: Date.now(), product_code: '', description: '', quantity: 1, weight: 0 }]);
  const [collectionDate, setCollectionDate] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [savingTransfer, setSavingTransfer] = useState(false);
  
  // Add to Goods In dialog state
  const [showAddToGoodsInDialog, setShowAddToGoodsInDialog] = useState(false);
  const [pendingTransferId, setPendingTransferId] = useState(null);
  const [pendingBranchName, setPendingBranchName] = useState(null); // Branch name for display
  
  // Weight tracking
  const [productWeights, setProductWeights] = useState({}); // Map of product_code -> weight
  
  // Calculate total transfer weight
  const totalTransferWeight = transferItems.reduce((sum, item) => {
    const weight = item.weight || productWeights[item.product_code?.toUpperCase()] || 0;
    const qty = parseInt(item.quantity) || 0;
    return sum + (weight * qty);
  }, 0);
  
  // Stock data for display
  const [stockData, setStockData] = useState({}); // Map of product_code -> stock info
  
  // View/Edit transfer state
  const [viewingTransfer, setViewingTransfer] = useState(null);
  const [editingTransfer, setEditingTransfer] = useState(false);

  // Latest transfer lines, readable synchronously so two quick clicks in the Recent Orders
  // panel can never both add the same product.
  const transferItemsRef = useRef(transferItems);
  transferItemsRef.current = transferItems;

  // "Requested By" defaults to the logged-in user's username (e.g. j.moseley) whenever a NEW
  // transfer is opened. Editing an existing transfer keeps its saved requester, and anything
  // the user has already typed is never overwritten.
  useEffect(() => {
    if (!showCreateModal || editingTransfer) return;
    const uname = String((user && user.username) || '').trim() || String((user && user.email) || '').split('@')[0];
    if (!uname) return;
    setRequestedBy((prev) => (prev && prev.trim() ? prev : uname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, editingTransfer, user && user.username]);
  
  // Upcoming transfers from transport
  const [upcomingTransfers, setUpcomingTransfers] = useState([]);
  
  // Upcoming transfer detail modal
  const [selectedUpcoming, setSelectedUpcoming] = useState(null);
  
  // PDF preview modal
  const [pdfPreviewTransfer, setPdfPreviewTransfer] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  
  // P.O. Expense linking state
  const [showPOModal, setShowPOModal] = useState(false);
  const [linkedPO, setLinkedPO] = useState(null);
  const [poFormData, setPOFormData] = useState({
    supplier: '',
    order_date: new Date().toISOString().split('T')[0],
    ordered_by: '',
    m4_order_no: '',
    notes: '',
    total_cost: 0,
    customer_recharge_amount: 0,
    ho_recharge_amount: 0,
    po_type: 'branch_expense',
  });
  
  // View linked P.O. state
  const [viewingLinkedPO, setViewingLinkedPO] = useState(null);
  const [showLinkedPOModal, setShowLinkedPOModal] = useState(false);
  const [editingLinkedPO, setEditingLinkedPO] = useState(false);
  const [editPOData, setEditPOData] = useState(null);
  const [savingPOEdit, setSavingPOEdit] = useState(false);
  // Store the transfer that has this linked P.O. so we can edit it too
  const [linkedTransferForPO, setLinkedTransferForPO] = useState(null);

  // ========== TRUNKING TRANSFER STATE ==========
  const [isTrunkingMode, setIsTrunkingMode] = useState(false);
  const [trunkingBranches, setTrunkingBranches] = useState([]); // Array of { branch, order }
  const [showTrunkingSetupModal, setShowTrunkingSetupModal] = useState(false);
  const [trunkingLegs, setTrunkingLegs] = useState([]); // Array of leg configurations
  const [trunkingName, setTrunkingName] = useState('');
  const [savingTrunking, setSavingTrunking] = useState(false);
  const [recentTrunkingTransfers, setRecentTrunkingTransfers] = useState([]);
  const [viewingTrunking, setViewingTrunking] = useState(null);
  const [currentLegIndex, setCurrentLegIndex] = useState(0); // For adding items per leg
  const [editingTrunkingId, setEditingTrunkingId] = useState(null); // For editing existing trunking transfer
  const [showTrunkingPdfPreview, setShowTrunkingPdfPreview] = useState(false); // PDF preview for trunking
  const [trunkingPdfHtml, setTrunkingPdfHtml] = useState(''); // HTML content for trunking PDF preview
  const [branchAddresses, setBranchAddresses] = useState({}); // Branch ID -> address data lookup
  const [externalBranches, setExternalBranches] = useState(BRANCHES); // External branches for transfers - initially use hardcoded, then load from API

  // Trunking photo state
  const [photoModalTransferId, setPhotoModalTransferId] = useState(null);
  const [photoModalPhotos, setPhotoModalPhotos] = useState([]);
  const [photoModalLoading, setPhotoModalLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [fullSizePhoto, setFullSizePhoto] = useState(null);
  const photoModalFileRef = useRef(null);

  // Quick Links state
  const [quickLinks, setQuickLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);

  useEffect(() => {
    loadRecentTransfers();
    loadTodayStockData();
    loadUpcomingTransfers();
    loadRecentTrunkingTransfers();
    loadQuickLinks();
    loadLinkGroups();
    loadBranchAddresses();
    loadExternalBranches();
  }, [effectiveBranchId]);

  // If GlobalSearch sent us here with a "Transfer from <branch>" prefill (see
  // handleTransferToBranch in GlobalSearch.jsx), open a new transfer for that
  // branch with the product already added, as soon as the branch list is ready.
  const transferPrefillProcessedRef = useRef(false);
  useEffect(() => {
    if (transferPrefillProcessedRef.current) return;
    const raw = sessionStorage.getItem('stock_transfer_prefill');
    if (!raw || !externalBranches || externalBranches.length === 0) return;
    try {
      const prefill = JSON.parse(raw);
      const branch = externalBranches.find(b => b.branchCode === prefill.branchCode);
      if (branch) {
        transferPrefillProcessedRef.current = true;
        sessionStorage.removeItem('stock_transfer_prefill');
        resetCreateForm();
        setSelectedBranch(branch);
        setTransferItems([{
          id: Date.now(),
          product_code: prefill.product_code || '',
          description: prefill.description || '',
          quantity: 1,
          stock_unit: prefill.stock_unit || 'EACH',
          weight: prefill.weight || 0
        }]);
        setShowCreateModal(true);
        toast.success(`New transfer started from ${branch.name}`);
      }
    } catch (error) {
      console.error('Failed to process stock transfer prefill:', error);
      sessionStorage.removeItem('stock_transfer_prefill');
    }
  }, [externalBranches]);

  // If Stock Efficiency sent us here to VIEW an existing transfer (see
  // handleViewExistingTransfer in StockEfficiencyView.jsx), fetch it and open
  // the read-only Transfer Request modal for it.
  const transferViewProcessedRef = useRef(false);
  useEffect(() => {
    if (transferViewProcessedRef.current) return;
    const transferId = sessionStorage.getItem('stock_transfer_view_id');
    if (!transferId) return;
    transferViewProcessedRef.current = true;
    sessionStorage.removeItem('stock_transfer_view_id');
    (async () => {
      try {
        const res = await api.get(`${API}/api/stock-transfers/${transferId}`);
        setViewingTransfer(res.data);
      } catch (error) {
        console.error('Failed to load transfer for viewing:', error);
        toast.error('Could not load that transfer');
      }
    })();
  }, []);

  // Reload branch addresses when viewing trunking modal opens
  useEffect(() => {
    if (viewingTrunking) {
      loadBranchAddresses();
    }
  }, [viewingTrunking]);

  // Load external branches from API (branch directory)
  const loadExternalBranches = async () => {
    try {
      const response = await api.get(`${API}/api/branch-directory?exclude_cp=true`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data && response.data.length > 0) {
        // Transform API data to match the BRANCHES format
        const branches = response.data.map(b => ({
          id: b.id,
          name: b.name,
          nickname: b.nickname || '',
          contact: b.default_contact || '',
          address1: b.address1 || '',
          address2: b.address2 || '',
          city: b.city || '',
          postcode: b.postcode || '',
          phone: b.phone || '',
          email: b.email || '',
          branchCode: b.branchCode || null
        }));
        setExternalBranches(branches);
      }
    } catch (error) {
      console.log('Using default branch list:', error);
      // Keep using hardcoded BRANCHES as fallback
    }
  };

  // Load branch addresses for display in trunking transfers
  const loadBranchAddresses = async () => {
    try {
      // Try to load from branch directory first (has detailed address info)
      const directoryResponse = await api.get(`${API}/api/branch-directory?exclude_cp=true`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const lookup = {};
      
      // Helper to add branch data under multiple keys for flexible lookup
      const addBranchToLookup = (branch, branchData) => {
        // Add by id
        if (branch.id) {
          lookup[branch.id] = branchData;
          lookup[branch.id.toLowerCase()] = branchData;
        }
        // Add by name (full name like "Encon Birmingham")
        if (branch.name) {
          lookup[branch.name] = branchData;
          lookup[branch.name.toLowerCase()] = branchData;
        }
        // Add by nickname (short name like "Birmingham")
        if (branch.nickname) {
          lookup[branch.nickname] = branchData;
          lookup[branch.nickname.toLowerCase()] = branchData;
        }
        // Add by branchCode (system code like "C30", "E70")
        if (branch.branchCode) {
          lookup[branch.branchCode] = branchData;
          lookup[branch.branchCode.toLowerCase()] = branchData;
        }
      };
      
      // Load from branch directory (external branches for transfers)
      (directoryResponse.data || []).forEach(branch => {
        const branchData = {
          name: branch.name || branch.id,
          nickname: branch.nickname || '',
          address1: branch.address1 || '',
          address2: branch.address2 || '',
          city: branch.city || '',
          postcode: branch.postcode || '',
          phone: branch.phone || '',
          email: branch.email || '',
          default_contact: branch.default_contact || '',
          contacts: branch.contacts || []
        };
        addBranchToLookup(branch, branchData);
      });
      
      // Also load system branches (C30, E70 etc.)
      try {
        const branchesResponse = await api.get(`${API}/api/branches`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        (branchesResponse.data?.branches || branchesResponse.data || []).forEach(branch => {
          // Only add if not already in lookup
          if (!lookup[branch.id]) {
            const branchData = {
              name: branch.name || branch.display_name || branch.id,
              address1: branch.address1 || branch.billing_address || branch.address || '',
              address2: branch.address2 || '',
              city: branch.city || branch.billing_city || '',
              postcode: branch.postcode || branch.billing_code || '',
              phone: branch.phone || branch.contact_phone || ''
            };
            addBranchToLookup(branch, branchData);
          }
        });
      } catch (err) {
        console.log('System branches not loaded:', err);
      }
      
      setBranchAddresses(lookup);
    } catch (error) {
      console.error('Error loading branch addresses:', error);
      // Fallback - try just system branches
      try {
        const response = await api.get(`${API}/api/branches`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const lookup = {};
        (response.data?.branches || response.data || []).forEach(branch => {
          lookup[branch.id] = {
            name: branch.name || branch.display_name || branch.id,
            address1: branch.address1 || branch.billing_address || branch.address || '',
            address2: branch.address2 || '',
            city: branch.city || branch.billing_city || '',
            postcode: branch.postcode || branch.billing_code || '',
            phone: branch.phone || branch.contact_phone || ''
          };
        });
        setBranchAddresses(lookup);
      } catch (fallbackError) {
        console.error('Fallback branch loading also failed:', fallbackError);
      }
    }
  };

  // Load quick links
  const loadQuickLinks = async () => {
    try {
      const response = await api.get(`${API}/api/quick-links`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setQuickLinks(response.data);
    } catch (error) {
      console.error('Error loading quick links:', error);
    }
  };

  // Load link groups
  const loadLinkGroups = async () => {
    try {
      const response = await api.get(`${API}/api/quick-link-groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLinkGroups(response.data);
    } catch (error) {
      console.error('Error loading link groups:', error);
    }
  };

  // Get links for a specific group
  const getLinksForGroup = (groupId) => {
    return quickLinks.filter(link => (link.group_id || 'default') === groupId).sort((a, b) => a.order - b.order);
  };

  // Handle quick link click
  const handleQuickLinkClick = (link) => {
    if (link.url.startsWith('http')) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link.url);
    }
  };

  // Load today's stock data (from free stock report uploaded today)
  const loadTodayStockData = async () => {
    try {
      const response = await api.get(`${API}/api/stock-transfers/today-stock`, {
        params: { view_as_branch: effectiveBranchId },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const products = response.data.products || [];
      const stockMap = {};
      products.forEach(p => {
        stockMap[p.product_code?.toUpperCase()] = p.on_hand_quantity || p.free_stock || 0;
      });
      setStockData(stockMap);
    } catch (error) {
      console.error('Error loading stock data:', error);
    }
  };

  // Load recent transfers
  const loadRecentTransfers = async () => {
    try {
      const response = await api.get(`${API}/api/stock-transfers/recent`, {
        params: { view_as_branch: effectiveBranchId },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRecentTransfers(response.data.transfers || []);
    } catch (error) {
      console.error('Error loading transfers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load recent trunking transfers
  const loadRecentTrunkingTransfers = async () => {
    try {
      const response = await api.get(`${API}/api/trunking-transfers/recent`, {
        params: { view_as_branch: effectiveBranchId, limit: 10 },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRecentTrunkingTransfers(response.data.grouped_transfers || []);
    } catch (error) {
      console.error('Error loading trunking transfers:', error);
    }
  };

  // Photo helpers
  const getPhotoUrl = (photoId) => {
    const token = localStorage.getItem('token');
    return `${API}/api/files/trunking-photo/${photoId}?auth=${token}`;
  };

  const openPhotoModal = async (transferId) => {
    setPhotoModalTransferId(transferId);
    setPhotoModalLoading(true);
    setPhotoModalPhotos([]);
    try {
      const resp = await api.get(`${API}/api/trunking-transfers/${transferId}/photos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const photos = resp.data.photos || [];
      setPhotoModalPhotos(photos);
    } catch (e) {
      console.error('Error loading photos:', e);
    }
    setPhotoModalLoading(false);
  };

  const handlePhotoUpload = async (transferId, files) => {
    if (!files || files.length === 0) return;
    setPhotoUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`${API}/api/trunking-transfers/${transferId}/photos`, formData, {
          headers: { 
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded`);
      // Refresh photos if modal is open
      if (photoModalTransferId === transferId) {
        openPhotoModal(transferId);
      }
      loadRecentTrunkingTransfers();
    } catch (e) {
      console.error('Error uploading photo:', e);
      toast.error('Failed to upload photo');
    }
    setPhotoUploading(false);
  };

  const handleDeletePhoto = async (transferId, photoId) => {
    try {
      await api.delete(`${API}/api/trunking-transfers/${transferId}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Photo deleted');
      // Clean up
      setPhotoModalPhotos(prev => prev.filter(p => p.id !== photoId));
      if (fullSizePhoto?.id === photoId) setFullSizePhoto(null);
      loadRecentTrunkingTransfers();
    } catch (e) {
      console.error('Error deleting photo:', e);
      toast.error('Failed to delete photo');
    }
  };

  // Load upcoming transfers from transport (entries with status='stock_transfer')
  const loadUpcomingTransfers = async () => {
    try {
      const response = await api.get(`${API}/api/stock-transfers/upcoming-from-transport`, {
        params: { view_as_branch: effectiveBranchId },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Also fetch trunking transfers that involve this branch
      const trunkingResponse = await api.get(`${API}/api/trunking-transfers`, {
        params: { view_as_branch: effectiveBranchId, limit: 20 },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const regularTransfers = response.data.transfers || [];
      const trunkingTransfers = trunkingResponse.data.transfers || [];
      
      // Convert trunking transfers to upcoming format for legs involving this branch
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const trunkingAsUpcoming = [];
      trunkingTransfers.forEach(trunk => {
        const legs = trunk.legs || [];
        legs.forEach(leg => {
          const legDate = leg.scheduled_date ? new Date(leg.scheduled_date) : null;
          if (legDate && legDate >= today) {
            // Check if this leg involves the current branch (either as source or destination)
            const currentBranch = externalBranches.find(b => b.id === effectiveBranchId);
            const currentBranchName = currentBranch?.name || '';
            
            if (leg.from_branch_name === currentBranchName || leg.to_branch_name === currentBranchName) {
              trunkingAsUpcoming.push({
                id: `trunk-${trunk.id}-leg-${leg.leg_number}`,
                trunk_id: trunk.id,
                trunk_name: trunk.name,
                date: leg.scheduled_date,
                branch_name: leg.to_branch_name,
                from_branch_name: leg.from_branch_name,
                vehicle_registration: 'Trunking',
                run_number: `Leg ${leg.leg_number}`,
                is_trunking: true,
                action_type: leg.action_type,
                action_by: leg.action_by,
                leg_number: leg.leg_number,
                total_legs: legs.length,
                full_route: legs.map((l, i) => i === 0 ? [l.from_branch_name, l.to_branch_name] : [l.to_branch_name]).flat()
              });
            }
          }
        });
      });
      
      // Combine and sort by date
      const allUpcoming = [...regularTransfers, ...trunkingAsUpcoming].sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      );
      
      setUpcomingTransfers(allUpcoming);
    } catch (error) {
      console.error('Error loading upcoming transfers:', error);
    }
  };

  // Load PDF for preview
  const loadPdfPreview = async (transfer) => {
    setLoadingPdf(true);
    setPdfPreviewTransfer(transfer);
    try {
      const response = await api.get(`${API}/api/stock-transfers/${transfer.id}/pdf`, {
        params: { view_as_branch: effectiveBranchId },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setPdfUrl(url);
    } catch (error) {
      console.error('Error loading PDF:', error);
      toast.error('Failed to load PDF preview');
      setPdfPreviewTransfer(null);
    } finally {
      setLoadingPdf(false);
    }
  };

  // Print transfer PDF
  const printTransfer = async (transfer) => {
    try {
      const response = await api.get(`${API}/api/stock-transfers/${transfer.id}/pdf`, {
        params: { view_as_branch: effectiveBranchId },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error('Error printing:', error);
      toast.error('Failed to print transfer');
    }
  };

  // Navigate to transport view for a specific date
  const navigateToTransportDate = (date) => {
    navigate(`/transport?date=${date}`);
  };

  // Load transfers for branch history
  const loadBranchHistory = async (branchId, month, year) => {
    setLoadingHistory(true);
    try {
      const response = await api.get(`${API}/api/stock-transfers/by-branch`, {
        params: { branch_id: branchId, month: month + 1, year, view_as_branch: effectiveBranchId },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setBranchTransfers(response.data.transfers || []);
    } catch (error) {
      console.error('Error loading branch history:', error);
      setBranchTransfers([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Select product from ProductLookup component
  const handleSelectProduct = async (index, product) => {
    const productCode = (product.product_code || product.code || '').toUpperCase();
    
    // Try to get weight from product or fetch from additional product data
    let weight = product.weight || 0;
    if (!weight && productCode) {
      try {
        const response = await api.get(`${API}/api/stock-database/product-weight/${productCode}`);
        if (response.data.found) {
          weight = response.data.weight || 0;
        }
      } catch (error) {
        console.log('Could not fetch product weight:', error);
      }
    }
    
    // Update product weights map
    if (weight > 0) {
      setProductWeights(prev => ({ ...prev, [productCode]: weight }));
    }
    
    const updatedItems = [...transferItems];
    updatedItems[index] = {
      ...updatedItems[index],
      product_code: productCode,
      description: product.description || product.product_description || product.name || '',
      stock_unit: product.stock_unit || product.stocking_unit || 'EACH',
      weight: weight
    };
    setTransferItems(updatedItems);
  };

  // Update item field (for non-product fields like quantity)
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...transferItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setTransferItems(updatedItems);
  };

  // Add new line
  const addNewLine = () => {
    setTransferItems(prev => [...prev, { id: Date.now(), product_code: '', description: '', quantity: 1, weight: 0 }]);
  };

  // Remove line
  const removeLine = (index) => {
    if (transferItems.length > 1 && index > 0) {
      setTransferItems(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Add one line from the Recent Orders slide-out onto the transfer (weight looked up the same
  // way as when a product is picked in the product lookup).
  const handleAddOrderLine = async (line) => {
    const result = addOrderLineToItems(transferItemsRef.current, line);
    if (!result.added) {
      if (result.reason === 'duplicate') toast.info(`${result.code} is already on this transfer`);
      return false;
    }
    transferItemsRef.current = result.items;
    setTransferItems(result.items);
    if (result.code) {
      try {
        const response = await api.get(`${API}/api/stock-database/product-weight/${result.code}`);
        if (response.data && response.data.found && response.data.weight > 0) {
          const w = response.data.weight;
          setProductWeights(prev => ({ ...prev, [result.code]: w }));
          setTransferItems(prev => prev.map(i =>
            String(i.product_code || '').toUpperCase() === result.code && !i.weight ? { ...i, weight: w } : i
          ));
        }
      } catch (error) {
        console.log('Could not fetch product weight:', error);
      }
    }
    return true;
  };

  // Save transfer
  const handleSaveTransfer = async () => {
    if (!selectedBranch) {
      toast.error('Please select a branch');
      return;
    }
    if (!requestedBy.trim()) {
      toast.error('Please enter the requester name');
      return;
    }
    
    // Filter out empty lines
    const validItems = transferItems.filter(item => item.product_code || item.description);
    if (validItems.length === 0) {
      toast.error('Please add at least one product');
      return;
    }

    // Store the editing transfer ID BEFORE any state changes
    const isEditing = editingTransfer;
    const transferId = editingTransfer?.id;

    setSavingTransfer(true);
    try {
      // Build items with weight information
      const itemsWithWeights = validItems.map(item => {
        const unitWeight = item.weight || productWeights[item.product_code?.toUpperCase()] || 0;
        const qty = parseInt(item.quantity) || 1;
        return {
          product_code: item.product_code,
          description: item.description,
          stock_unit: item.stock_unit || 'EACH',
          quantity_required: qty,
          unit_weight: unitWeight,
          line_weight: unitWeight * qty
        };
      });
      
      const transferData = {
        branch_id: selectedBranch.id,
        branch_name: selectedBranch.name,
        branch_contact: selectedBranch.contact,
        branch_address: `${selectedBranch.address1}, ${selectedBranch.address2}, ${selectedBranch.city}, ${selectedBranch.postcode}`,
        branch_phone: selectedBranch.phone,
        requested_by: requestedBy,
        collection_date: collectionDate || null,
        notes: transferNotes,
        items: itemsWithWeights,
        // Include linked P.O. if exists
        linked_po_id: linkedPO?.id || null,
        linked_po_number: linkedPO?.po_number || null,
        total_weight: totalTransferWeight
      };

      let response;
      if (isEditing && transferId) {
        response = await api.put(`${API}/api/stock-transfers/${transferId}`, transferData, {
          params: { view_as_branch: effectiveBranchId },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Transfer updated successfully');
        setShowCreateModal(false);
        resetCreateForm();
        loadRecentTransfers();
        setViewingTransfer(response.data.transfer);
        setEditingTransfer(false);
      } else {
        response = await api.post(`${API}/api/stock-transfers`, transferData, {
          params: { view_as_branch: effectiveBranchId },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Transfer created successfully');
        
        // Completed — moves from "In Progress" into "Recents".
        if (modalUiV2 && stActivityIdRef.current) {
          activityBar.logRecentView({
            type: 'stock_transfer',
            activityId: stActivityIdRef.current,
            title: `Transfer - ${selectedBranch.name}`,
            subtitle: 'Completed',
            snapshot: { activityId: stActivityIdRef.current },
          });
          activityBar.discardActive();
          stActivityIdRef.current = null;
        }
        
        // Store the transfer ID and branch name for the dialog
        setPendingTransferId(response.data.transfer.id);
        setPendingBranchName(selectedBranch.name);
        
        setShowCreateModal(false);
        resetCreateForm();
        loadRecentTransfers();
        setViewingTransfer(response.data.transfer);
        setEditingTransfer(false);
        
        // Show the add to goods in dialog
        setShowAddToGoodsInDialog(true);
      }
      
    } catch (error) {
      console.error('Error saving transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to save transfer');
    } finally {
      setSavingTransfer(false);
    }
  };
  
  // Add transfer to Goods In
  const handleAddToGoodsIn = async (targetBranch) => {
    if (!pendingTransferId) return;
    
    try {
      await api.post(`${API}/api/stock-transfers/${pendingTransferId}/add-to-goods-in?target_branch=${targetBranch}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`Added to ${targetBranch} Goods In`);
    } catch (error) {
      console.error('Error adding to goods in:', error);
      toast.error(error.response?.data?.detail || 'Failed to add to Goods In');
    } finally {
      setShowAddToGoodsInDialog(false);
      setPendingTransferId(null);
      setPendingBranchName(null);
    }
  };

  // Delete transfer with orphan cleanup option
  const handleDeleteTransfer = async () => {
    if (!viewingTransfer) return;
    
    const hasLinkedPO = viewingTransfer.linked_po_id;
    
    // Different confirmation based on whether there's a linked P.O.
    if (hasLinkedPO) {
      const cancelPO = confirm(
        `This transfer has a linked P.O. (${viewingTransfer.linked_po_number}).\n\n` +
        `Do you want to CANCEL the linked P.O. as well?\n\n` +
        `• Click OK to delete the transfer AND cancel the P.O. (sets cost to £0, adds cancelled note)\n` +
        `• Click Cancel to delete ONLY the transfer (P.O. will be unlinked but remain active)`
      );
      
      try {
        await api.delete(
          `${API}/api/stock-transfers/${viewingTransfer.id}?cancel_linked_po=${cancelPO}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
        );
        
        if (cancelPO) {
          toast.success('Transfer deleted and linked P.O. cancelled');
        } else {
          toast.success('Transfer deleted (P.O. unlinked)');
        }
        setViewingTransfer(null);
        loadRecentTransfers();
      } catch (error) {
        console.error('Error deleting transfer:', error);
        toast.error('Failed to delete transfer');
      }
    } else {
      if (!confirm('Are you sure you want to delete this transfer request?')) return;
      
      try {
        await api.delete(`${API}/api/stock-transfers/${viewingTransfer.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Transfer deleted');
        setViewingTransfer(null);
        loadRecentTransfers();
      } catch (error) {
        console.error('Error deleting transfer:', error);
        toast.error('Failed to delete transfer');
      }
    }
  };

  // Send transfer via email
  const handleSendTransfer = async () => {
    if (!viewingTransfer) return;
    
    try {
      // Download PDF
      const response = await api.get(`${API}/api/stock-transfers/${viewingTransfer.id}/pdf`, {
        params: { view_as_branch: effectiveBranchId },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Transfer_${viewingTransfer.transfer_ref || viewingTransfer.branch_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Open email client - use branch-aware details
      const branch = externalBranches.find(b => b.id === viewingTransfer.branch_id);
      const ourDirectoryBranch = externalBranches.find(b => b.branchCode === effectiveBranchId);
      const ourBranch = ourDirectoryBranch
        ? { ...getOurBranch(effectiveBranchId), name: ourDirectoryBranch.name }
        : getOurBranch(effectiveBranchId);
      const subject = encodeURIComponent(`${effectiveBranchId} Stock Transfer Request - ${new Date().toLocaleDateString('en-GB')}`);
      const body = encodeURIComponent(`Hi ${branch?.contact || 'Team'},\n\nPlease find attached our stock transfer request.\n\nKind regards,\n${viewingTransfer.requested_by}\n${ourBranch.name}`);
      window.open(`mailto:?subject=${subject}&body=${body}`);
      
      toast.success('PDF downloaded - please attach to your email');
    } catch (error) {
      console.error('Error sending transfer:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Reset create form
  const resetCreateForm = () => {
    setSelectedBranch(null);
    setRequestedBy('');
    setTransferItems([{ id: Date.now(), product_code: '', description: '', quantity: 1, weight: 0 }]);
    setCollectionDate('');
    setTransferNotes('');
    setEditingTransfer(false);
    setLinkedPO(null); // Reset linked P.O.
    setProductWeights({}); // Reset product weights
    setBranchSearch('');
    setShowRecentOrders(false);
  };

  // Modal UI v2 — register as a resumable "In Progress" activity once the
  // create modal is open for a genuinely NEW transfer (not an edit).
  useEffect(() => {
    if (!showCreateModal || editingTransfer || !modalUiV2 || stActivityIdRef.current) return;
    stActivityIdRef.current = `stock-transfer-${Date.now()}`;
    activityBar.openActivity({
      type: 'stock_transfer',
      activityId: stActivityIdRef.current,
      title: 'New Stock Transfer',
      subtitle: 'Stock Transfer',
      snapshot: { activityId: stActivityIdRef.current },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, editingTransfer, modalUiV2]);

  // Keep the live snapshot fresh as the transfer is filled in.
  useEffect(() => {
    if (!showCreateModal || editingTransfer || !modalUiV2 || !stActivityIdRef.current) return;
    activityBar.updateActiveSnapshot(
      { activityId: stActivityIdRef.current },
      { title: `Transfer - ${selectedBranch?.name || 'New'}`, subtitle: 'Stock Transfer' }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, editingTransfer, modalUiV2, selectedBranch]);

  // Edit transfer
  const handleEditTransfer = async () => {
    if (!viewingTransfer) return;
    
    // Store the transfer we're editing before clearing viewingTransfer
    const transferToEdit = viewingTransfer;
    
    const branch = externalBranches.find(b => b.id === transferToEdit.branch_id);
    setSelectedBranch(branch);
    setRequestedBy(transferToEdit.requested_by);
    setCollectionDate(transferToEdit.collection_date || '');
    setTransferNotes(transferToEdit.notes || '');
    
    // Build product weights map from existing items
    const weightsMap = {};
    transferToEdit.items.forEach(item => {
      if (item.unit_weight > 0) {
        weightsMap[item.product_code?.toUpperCase()] = item.unit_weight;
      }
    });
    setProductWeights(prev => ({ ...prev, ...weightsMap }));
    
    setTransferItems(transferToEdit.items.map((item, idx) => ({
      id: idx,
      product_code: item.product_code,
      description: item.description,
      quantity: item.quantity_required,
      stock_unit: item.stock_unit,
      weight: item.unit_weight || 0
    })));
    
    // Set linked P.O. if exists
    if (transferToEdit.linked_po_id) {
      // Fetch full P.O. details first
      try {
        const token = localStorage.getItem('token');
        const response = await api.get(`${API}/api/po-expenses/${transferToEdit.linked_po_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          setLinkedPO(response.data);
        } else {
          // Fallback to minimal info
          setLinkedPO({
            id: transferToEdit.linked_po_id,
            po_number: transferToEdit.linked_po_number || 'P.O.',
            supplier: '',
            total_cost: 0
          });
        }
      } catch (error) {
        console.error('Error fetching linked P.O.:', error);
        // Fallback to minimal info
        setLinkedPO({
          id: transferToEdit.linked_po_id,
          po_number: transferToEdit.linked_po_number || 'P.O.',
          supplier: '',
          total_cost: 0
        });
      }
    } else {
      setLinkedPO(null);
    }
    
    // Set editingTransfer BEFORE opening the modal
    setEditingTransfer(transferToEdit);
    // Open the create/edit modal BEFORE closing the view modal
    setShowCreateModal(true);
    // Close the view modal last
    setViewingTransfer(null);
  };

  // Open branch history
  const handleOpenBranchHistory = (branch) => {
    setSelectedHistoryBranch(branch);
    setHistoryMonth(new Date().getMonth());
    setHistoryYear(new Date().getFullYear());
    loadBranchHistory(branch.id, new Date().getMonth(), new Date().getFullYear());
    setShowBranchHistoryModal(true);
  };

  // Create transfer for branch from history modal
  const handleCreateTransferForBranch = () => {
    setShowBranchHistoryModal(false);
    setSelectedBranch(selectedHistoryBranch);
    setShowCreateModal(true);
  };

  // P.O. Types for dropdown
  const PO_TYPES = [
    { value: 'branch_expense', label: 'Branch Expense (Ex Transport)' },
    { value: 'staff_cv_hire', label: 'Staff CV Hire' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'stationery', label: 'Stationery' },
    { value: 'other', label: 'Other' },
  ];

  // Save linked P.O. expense
  const handleSavePO = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // If we're editing an existing P.O., update it
      if (linkedPO && linkedPO.id) {
        const response = await api.put(`${API}/api/po-expenses/${linkedPO.id}`, poFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const updatedPO = response.data.expense || response.data;
        setLinkedPO(updatedPO);
        setShowPOModal(false);
        toast.success(`P.O. ${updatedPO.po_number} updated!`);
      } else {
        // Create new P.O. - auto-set notes to "{branch_name} Transfer" if branch is selected
        const poDataToSave = { ...poFormData };
        if (selectedBranch && !poDataToSave.notes) {
          poDataToSave.notes = `${selectedBranch.name} Transfer`;
        }
        
        const response = await api.post(`${API}/api/po-expenses`, poDataToSave, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Backend returns {success, expense, po_number} - extract the expense
        const savedPO = response.data.expense || response.data;
        setLinkedPO(savedPO);
        setShowPOModal(false);
        
        // Auto-populate transfer notes with supplier and P.O. number
        const poInfo = `${poFormData.supplier} - ${savedPO.po_number}`;
        setTransferNotes(prev => prev ? `${prev}\n${poInfo}` : poInfo);
        
        toast.success(`P.O. ${savedPO.po_number} created and linked!`);
      }
    } catch (error) {
      toast.error('Failed to save P.O.: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Open P.O. modal for editing linked P.O.
  const handleEditLinkedPO = () => {
    if (linkedPO) {
      setPOFormData({
        supplier: linkedPO.supplier || '',
        order_date: linkedPO.order_date || new Date().toISOString().split('T')[0],
        ordered_by: linkedPO.ordered_by || '',
        m4_order_no: linkedPO.m4_order_no || '',
        notes: linkedPO.notes || '',
        total_cost: linkedPO.total_cost || 0,
        customer_recharge_amount: linkedPO.customer_recharge_amount || 0,
        ho_recharge_amount: linkedPO.ho_recharge_amount || 0,
        po_type: linkedPO.po_type || 'branch_expense',
      });
      setShowPOModal(true);
    }
  };
  
  // View linked P.O. from transfer card - also store the transfer info
  const handleViewLinkedPO = async (poId, transfer = null) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API}/api/po-expenses/${poId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setViewingLinkedPO(response.data);
        setLinkedTransferForPO(transfer);
        setEditingLinkedPO(false);
        setEditPOData(null);
        setShowLinkedPOModal(true);
      } else {
        toast.error('P.O. not found');
      }
    } catch (error) {
      console.error('Error loading P.O.:', error);
      toast.error('Failed to load P.O. details');
    }
  };
  
  // Start editing the linked P.O.
  const handleStartEditPO = () => {
    if (viewingLinkedPO) {
      setEditPOData({
        supplier: viewingLinkedPO.supplier || '',
        order_date: viewingLinkedPO.order_date || '',
        ordered_by: viewingLinkedPO.ordered_by || '',
        m4_order_no: viewingLinkedPO.m4_order_no || '',
        notes: viewingLinkedPO.notes || '',
        total_cost: viewingLinkedPO.total_cost || 0,
        customer_recharge_amount: viewingLinkedPO.customer_recharge_amount || 0,
        ho_recharge_amount: viewingLinkedPO.ho_recharge_amount || 0,
        po_type: viewingLinkedPO.po_type || 'branch_expense',
      });
      setEditingLinkedPO(true);
    }
  };
  
  // Save P.O. edits
  const handleSavePOEdit = async () => {
    if (!viewingLinkedPO || !editPOData) return;
    
    setSavingPOEdit(true);
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/api/po-expenses/${viewingLinkedPO.id}`, editPOData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('P.O. updated successfully');
      
      // Refresh the P.O. data
      const refreshResponse = await api.get(`${API}/api/po-expenses/${viewingLinkedPO.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewingLinkedPO(refreshResponse.data);
      setEditingLinkedPO(false);
      setEditPOData(null);
      
      // Reload transfers to reflect any changes
      loadRecentTransfers();
    } catch (error) {
      console.error('Error updating P.O.:', error);
      toast.error('Failed to update P.O.');
    } finally {
      setSavingPOEdit(false);
    }
  };
  
  // Edit the linked transfer from the P.O. modal
  const handleEditLinkedTransferFromPO = () => {
    if (linkedTransferForPO) {
      setShowLinkedPOModal(false);
      setViewingTransfer(linkedTransferForPO);
    }
  };

  // Open blank P.O. modal
  const handleOpenPOModal = () => {
    setPOFormData({
      supplier: '',
      order_date: new Date().toISOString().split('T')[0],
      ordered_by: requestedBy || '',
      m4_order_no: '',
      notes: '',
      total_cost: 0,
      customer_recharge_amount: 0,
      ho_recharge_amount: 0,
      po_type: 'branch_expense',
    });
    setShowPOModal(true);
  };

  // Change history month
  const changeHistoryMonth = (delta) => {
    let newMonth = historyMonth + delta;
    let newYear = historyYear;
    
    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    } else if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }
    
    setHistoryMonth(newMonth);
    setHistoryYear(newYear);
    loadBranchHistory(selectedHistoryBranch.id, newMonth, newYear);
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // ========== TRUNKING TRANSFER FUNCTIONS ==========
  
  // Get display name for the current user
  const getUserDisplayName = () => {
    if (!user) return '';
    if (user.full_name && user.full_name.trim()) {
      return user.full_name.trim();
    }
    if (user.username && user.username.trim()) {
      return user.username.trim();
    }
    if (user.email) {
      return user.email.split('@')[0];
    }
    return '';
  };

  // Toggle branch selection in trunking mode
  const toggleTrunkingBranch = (branch) => {
    const existingIndex = trunkingBranches.findIndex(b => b.branch.id === branch.id);
    
    if (existingIndex >= 0) {
      // Remove this branch and renumber remaining
      const updated = trunkingBranches
        .filter(b => b.branch.id !== branch.id)
        .map((b, idx) => ({ ...b, order: idx + 1 }));
      setTrunkingBranches(updated);
    } else {
      // Add new branch with next order number
      setTrunkingBranches([...trunkingBranches, { branch, order: trunkingBranches.length + 1 }]);
    }
  };

  // Get branch order number for display
  const getBranchOrder = (branchId) => {
    const found = trunkingBranches.find(b => b.branch.id === branchId);
    return found ? found.order : null;
  };

  // Open trunking setup modal after branch selection
  const openTrunkingSetup = () => {
    if (trunkingBranches.length < 2) {
      toast.error('Select at least 2 branches for a trunking transfer');
      return;
    }
    
    // Sort branches by order
    const sorted = [...trunkingBranches].sort((a, b) => a.order - b.order);
    
    // Get current user's name for auto-fill
    const currentUserName = getUserDisplayName();
    
    // Create legs from consecutive branches
    const legs = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      legs.push({
        leg_number: i + 1,
        from_branch_id: sorted[i].branch.id,
        from_branch_name: sorted[i].branch.name,
        to_branch_id: sorted[i + 1].branch.id,
        to_branch_name: sorted[i + 1].branch.name,
        action_type: 'collect', // Default: destination collects
        action_by: currentUserName, // Auto-fill with current user's name
        scheduled_date: '',
        items: [],
        notes: ''
      });
    }
    
    setTrunkingLegs(legs);
    setShowCreateModal(false);
    setShowTrunkingSetupModal(true);
  };

  // Update a specific leg
  const updateTrunkingLeg = (legIndex, field, value) => {
    const updated = [...trunkingLegs];
    updated[legIndex] = { ...updated[legIndex], [field]: value };
    setTrunkingLegs(updated);
  };

  // Add item to a specific leg
  const addItemToLeg = (legIndex, item) => {
    const updated = [...trunkingLegs];
    updated[legIndex].items = [...(updated[legIndex].items || []), item];
    setTrunkingLegs(updated);
  };

  // Remove item from a leg
  const removeItemFromLeg = (legIndex, itemIndex) => {
    const updated = [...trunkingLegs];
    updated[legIndex].items = updated[legIndex].items.filter((_, i) => i !== itemIndex);
    setTrunkingLegs(updated);
  };

  // Save trunking transfer
  const saveTrunkingTransfer = async () => {
    // Validate
    for (let i = 0; i < trunkingLegs.length; i++) {
      const leg = trunkingLegs[i];
      if (!leg.action_by) {
        toast.error(`Please specify who will ${leg.action_type === 'collect' ? 'collect' : 'deliver'} for leg ${i + 1}`);
        return;
      }
      if (!leg.scheduled_date) {
        toast.error(`Please set a date for leg ${i + 1}`);
        return;
      }
    }
    
    // Check first leg has items
    if (!trunkingLegs[0]?.items?.length) {
      toast.error('Please add at least one product to the first leg');
      return;
    }

    // Build the route string for the automatic note
    const routeBranches = [];
    trunkingLegs.forEach((leg, idx) => {
      if (idx === 0) {
        routeBranches.push(leg.from_branch_name.replace('Encon ', '').replace('Nevill Long ', ''));
      }
      routeBranches.push(leg.to_branch_name.replace('Encon ', '').replace('Nevill Long ', ''));
    });
    const routeString = routeBranches.join(' > ');
    
    // Create the automatic trunking note
    const autoNote = `This is part of a trunking run between branches. Please ensure stock transfers follow the route outlined in this document (${routeString})`;
    
    // Combine auto note with any user notes
    const finalNotes = transferNotes 
      ? `${autoNote}\n\n${transferNotes}`
      : autoNote;

    setSavingTrunking(true);
    try {
      if (editingTrunkingId) {
        // Update existing trunking transfer
        await api.put(`${API}/api/trunking-transfers/${editingTrunkingId}`, {
          name: trunkingName || `Trunk-${new Date().toISOString().slice(0, 10)}`,
          legs: trunkingLegs,
          notes: finalNotes
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        toast.success('Trunking transfer updated successfully!');
      } else {
        // Create new trunking transfer
        await api.post(`${API}/api/trunking-transfers`, {
          name: trunkingName || `Trunk-${new Date().toISOString().slice(0, 10)}`,
          legs: trunkingLegs,
          notes: finalNotes
        }, {
          params: { view_as_branch: effectiveBranchId },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        toast.success('Trunking transfer created successfully!');
      }
      
      setShowTrunkingSetupModal(false);
      resetTrunkingForm();
      loadRecentTrunkingTransfers();
    } catch (error) {
      console.error('Error saving trunking transfer:', error);
      toast.error(editingTrunkingId ? 'Failed to update trunking transfer' : 'Failed to create trunking transfer');
    } finally {
      setSavingTrunking(false);
    }
  };

  // Reset trunking form
  const resetTrunkingForm = () => {
    setIsTrunkingMode(false);
    setTrunkingBranches([]);
    setTrunkingLegs([]);
    setTrunkingName('');
    setCurrentLegIndex(0);
    setTransferNotes('');
    setEditingTrunkingId(null);
  };

  // Edit trunking transfer
  const handleEditTrunking = () => {
    if (!viewingTrunking) return;
    
    // Set up for editing
    setEditingTrunkingId(viewingTrunking.id);
    setTrunkingName(viewingTrunking.name || '');
    setTrunkingLegs(viewingTrunking.legs || []);
    setTransferNotes(viewingTrunking.notes || '');
    
    // Close view modal and open setup modal
    setViewingTrunking(null);
    setShowTrunkingSetupModal(true);
  };

  // Delete trunking transfer
  const handleDeleteTrunking = async () => {
    if (!viewingTrunking) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete this trunking transfer "${viewingTrunking.name}"?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      await api.delete(`${API}/api/trunking-transfers/${viewingTrunking.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Trunking transfer deleted');
      setViewingTrunking(null);
      loadRecentTrunkingTransfers();
    } catch (error) {
      console.error('Error deleting trunking transfer:', error);
      toast.error('Failed to delete trunking transfer');
    }
  };

  // Generate trunking PDF HTML content
  const generateTrunkingPdfHtml = () => {
    if (!viewingTrunking) return '';
    
    const legs = viewingTrunking.legs || [];
    let legsHtml = '';
    
    legs.forEach((leg, idx) => {
      // Try multiple ways to find the branch address
      const fromAddr = branchAddresses[leg.from_branch_id] || 
                       branchAddresses[leg.from_branch_name] || 
                       branchAddresses[leg.from_branch_name?.toLowerCase()] ||
                       branchAddresses[leg.from_branch_id?.toLowerCase()] || {};
      const toAddr = branchAddresses[leg.to_branch_id] || 
                     branchAddresses[leg.to_branch_name] || 
                     branchAddresses[leg.to_branch_name?.toLowerCase()] ||
                     branchAddresses[leg.to_branch_id?.toLowerCase()] || {};
      
      const items = leg.cumulative_items || leg.items || [];
      let itemsHtml = items.map(item => 
        `<tr><td style="padding: 8px; border: 1px solid #ddd; width: 140px; white-space: nowrap;">${item.product_code}</td>
         <td style="padding: 8px; border: 1px solid #ddd;">${item.description}</td>
         <td style="padding: 8px; border: 1px solid #ddd; text-align: center; width: 60px;">${item.quantity}</td></tr>`
      ).join('');
      
      // Build address HTML
      const fromAddrLines = [fromAddr.address1, fromAddr.address2, [fromAddr.city, fromAddr.postcode].filter(Boolean).join(', '), fromAddr.phone ? `Tel: ${fromAddr.phone}` : ''].filter(Boolean);
      const toAddrLines = [toAddr.address1, toAddr.address2, [toAddr.city, toAddr.postcode].filter(Boolean).join(', '), toAddr.phone ? `Tel: ${toAddr.phone}` : ''].filter(Boolean);
      
      const fromAddrHtml = fromAddrLines.length > 0 ? fromAddrLines.map(line => `<div>${line}</div>`).join('') : '<div style="color: #999; font-style: italic;">No address on file</div>';
      const toAddrHtml = toAddrLines.length > 0 ? toAddrLines.map(line => `<div>${line}</div>`).join('') : '<div style="color: #999; font-style: italic;">No address on file</div>';
      
      legsHtml += `
        <div style="margin-bottom: 30px; page-break-inside: avoid;">
          <h3 style="background: #f3f4f6; padding: 10px; border-radius: 8px; margin-bottom: 15px;">
            Leg ${leg.leg_number}: ${leg.from_branch_name} → ${leg.to_branch_name}
          </h3>
          
          <div style="display: flex; gap: 20px; margin-bottom: 15px; padding: 15px; background: #f5f3ff; border-radius: 8px; border: 1px solid #c4b5fd;">
            <div style="flex: 1;">
              <div style="font-weight: bold; color: #7c3aed; margin-bottom: 5px;">FROM: ${leg.from_branch_name}</div>
              <div style="font-size: 12px; color: #666;">${fromAddrHtml}</div>
            </div>
            <div style="flex: 1;">
              <div style="font-weight: bold; color: #7c3aed; margin-bottom: 5px;">TO: ${leg.to_branch_name}</div>
              <div style="font-size: 12px; color: #666;">${toAddrHtml}</div>
            </div>
          </div>
          
          <p><strong>Action:</strong> ${leg.action_type === 'collect' ? leg.to_branch_name + ' Collects' : leg.from_branch_name + ' Delivers'}</p>
          <p><strong>By:</strong> ${leg.action_by || 'TBD'}</p>
          <p><strong>Date:</strong> ${leg.scheduled_date ? new Date(leg.scheduled_date).toLocaleDateString('en-GB') : 'TBD'}</p>
          <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
            <thead>
              <tr style="background: #ee7203; color: white;">
                <th style="padding: 10px; text-align: left; width: 140px;">Code</th>
                <th style="padding: 10px; text-align: left;">Description</th>
                <th style="padding: 10px; text-align: center; width: 60px;">Qty</th>
              </tr>
            </thead>
            <tbody>${itemsHtml}</tbody>
          </table>
        </div>
      `;
    });
    
    return `
      <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto;">
        <h1 style="color: #ee7203;">Trunking Transfer: ${viewingTrunking.name}</h1>
        <p style="color: #666;">Created: ${new Date(viewingTrunking.created_at).toLocaleDateString('en-GB')}</p>
        ${legsHtml}
        ${viewingTrunking.notes ? `<div style="margin-top: 20px; padding: 15px; background: #f3f4f6; border-radius: 8px;"><strong>Notes:</strong> ${viewingTrunking.notes}</div>` : ''}
      </div>
    `;
  };

  // View PDF preview for trunking transfer
  const handleViewTrunkingPdf = () => {
    if (!viewingTrunking) return;
    setTrunkingPdfHtml(generateTrunkingPdfHtml());
    setShowTrunkingPdfPreview(true);
  };

  // Download trunking transfer as PDF
  const handleDownloadTrunkingPdf = () => {
    if (!viewingTrunking) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to download PDF');
      return;
    }
    
    const html = generateTrunkingPdfHtml();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Trunking Transfer - ${viewingTrunking.name}</title>
        <style>
          body { margin: 0; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        ${html}
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); };
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Print trunking transfer (generates PDF for all legs)
  const handlePrintTrunking = async () => {
    handleDownloadTrunkingPdf();
  };

  // Send trunking transfer via email (also downloads PDF)
  const handleSendTrunking = () => {
    if (!viewingTrunking) return;
    
    const legs = viewingTrunking.legs || [];
    if (legs.length === 0) {
      toast.error('No legs to send');
      return;
    }
    
    // First, download the PDF
    handleDownloadTrunkingPdf();
    
    // Get all branches involved
    const branchNames = [];
    legs.forEach((leg, idx) => {
      if (idx === 0) branchNames.push(leg.from_branch_name);
      branchNames.push(leg.to_branch_name);
    });
    
    // Find branch contacts
    const branchEmails = [];
    branchNames.forEach(name => {
      const branch = externalBranches.find(b => b.name === name);
      if (branch && branch.email) {
        branchEmails.push(branch.email);
      }
    });
    
    // Build email body
    let emailBody = `Hi Team,\n\nPlease see details for Trunking Transfer: ${viewingTrunking.name}\n\n`;
    
    legs.forEach(leg => {
      emailBody += `Leg ${leg.leg_number}: ${leg.from_branch_name} → ${leg.to_branch_name}\n`;
      emailBody += `  Action: ${leg.action_type === 'collect' ? leg.to_branch_name + ' Collects' : leg.from_branch_name + ' Delivers'}\n`;
      emailBody += `  Date: ${leg.scheduled_date ? new Date(leg.scheduled_date).toLocaleDateString('en-GB') : 'TBD'}\n`;
      emailBody += `  By: ${leg.action_by || 'TBD'}\n\n`;
    });
    
    if (viewingTrunking.notes) {
      emailBody += `Notes: ${viewingTrunking.notes}\n\n`;
    }
    
    emailBody += `Kind regards`;
    
    const subject = `Trunking Transfer: ${viewingTrunking.name}`;
    // Open email without pre-filled addresses - user will add recipients
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Small delay to allow PDF to open first
    setTimeout(() => {
      window.location.href = mailtoUrl;
    }, 500);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  // Check if date is today
  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is in future
  const isFuture = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header with centered title */}
      <AppHeader 
        user={user}
        subHeaderLeft={
          <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
            <Truck className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Stock Transfer</span>
          </div>
        }
      />

      {/* Main Content */}
      <div className="pt-[20px] px-4 lg:px-6 pb-6 bg-gray-100 min-h-screen">
        <div className="max-w-[1800px] mx-auto">
          {/* Nav pills — now in AppHeader global header */}
          
          {/* Upcoming Transfers Card - Under title */}
          {upcomingTransfers.length > 0 && (
            <Card className="bg-white shadow-md mb-6 rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="h-5 w-5 text-gray-700" />
                  <span className="font-semibold text-base text-gray-900">Upcoming Transfers</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {upcomingTransfers.map((transfer, idx) => {
                    const transferDate = new Date(transfer.date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const isToday = transferDate.toDateString() === today.toDateString();
                    const isFuture = transferDate > today;
                    const isTrunking = transfer.is_trunking;
                    
                    return (
                      <div 
                        key={idx}
                        onClick={() => {
                          if (isTrunking) {
                            // Load the full trunking transfer to view
                            api.get(`${API}/api/trunking-transfers/${transfer.trunk_id}`, {
                              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                            }).then(res => {
                              setViewingTrunking(res.data);
                            }).catch(err => {
                              console.error('Error loading trunking transfer:', err);
                              toast.error('Failed to load trunking transfer');
                            });
                          } else {
                            setSelectedUpcoming(transfer);
                          }
                        }}
                        className={`p-3 rounded-xl cursor-pointer shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200 border-2 ${
                          isTrunking 
                            ? (isToday ? 'border-yellow-400 bg-orange-50' : 'border-orange-400 bg-orange-50')
                            : (isToday ? 'border-yellow-400 bg-white' : 
                               isFuture ? 'border-green-400 bg-white' : 'border-gray-300 bg-white')
                        }`}
                      >
                        {/* Header with branch name and date */}
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-semibold text-sm text-gray-900">
                            {isTrunking ? (
                              <div>
                                <span className="text-xs text-orange-600 font-bold uppercase">Trunking</span>
                                <br/>
                                {transfer.from_branch_name.replace('Encon ', '').replace('Nevill Long ', '')} → {transfer.branch_name.replace('Encon ', '').replace('Nevill Long ', '')}
                              </div>
                            ) : transfer.branch_name}
                          </div>
                          <div className={`text-right ${isToday ? 'text-yellow-700' : isTrunking ? 'text-orange-700' : 'text-green-700'}`}>
                            <div className="text-xs font-semibold uppercase">
                              {transferDate.toLocaleDateString('en-GB', { weekday: 'short' })}
                            </div>
                            <div className="text-lg font-bold">
                              {transferDate.toLocaleDateString('en-GB', { day: 'numeric' })}
                            </div>
                            <div className="text-xs font-semibold uppercase">
                              {transferDate.toLocaleDateString('en-GB', { month: 'short' })}
                            </div>
                          </div>
                        </div>
                        {/* Vehicle and Run info - prominently displayed */}
                        <div className={`mt-2 p-2 rounded-lg ${isTrunking ? 'bg-orange-100' : 'bg-blue-50'}`}>
                          <div className={`flex items-center gap-1 text-sm font-semibold ${isTrunking ? 'text-orange-800' : 'text-blue-800'}`}>
                            <Truck className="h-4 w-4" />
                            <span>{transfer.vehicle_registration || transfer.vehicle || 'No Vehicle'}</span>
                          </div>
                          {transfer.run_number && (
                            <div className={`text-xs mt-1 ${isTrunking ? 'text-orange-600' : 'text-blue-600'}`}>
                              {isTrunking ? transfer.run_number : `Run ${transfer.run_number}`}
                            </div>
                          )}
                          {isTrunking && transfer.action_type && (
                            <div className="text-xs text-orange-600 mt-1">
                              {transfer.action_type === 'collect' ? 'Collection' : 'Delivery'} by {transfer.action_by || 'TBD'}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add Transfer button — above Recent Transfers */}
          <div className="flex justify-end gap-2 mb-3">
            <Button
              onClick={() => navigate('/b29-stock-transfer')}
              variant="outline"
              className="flex items-center gap-2 font-semibold rounded-full px-4 py-2 border-2 hover:scale-[1.03] transition-all duration-200"
              style={{ borderColor: '#ee7203', color: '#ee7203', backgroundColor: 'white' }}
              data-testid="open-b29-transfers-btn"
            >
              <Package className="h-5 w-5" />
              <span>B29 Transfers</span>
            </Button>
            <Button
              onClick={() => {
                resetCreateForm();
                setShowCreateModal(true);
              }}
              variant="outline"
              className="flex items-center gap-2 font-semibold rounded-xl px-4 py-2 border-2"
              style={{ borderColor: enconOrange, color: enconOrange, backgroundColor: 'white' }}
              data-testid="create-transfer-btn"
            >
              <Plus className="h-5 w-5" />
              <span>Create Transfer</span>
            </Button>
          </div>

          {/* Recent Transfers - White box with rounded edges */}
          <Card className="bg-white shadow-md mb-6 rounded-xl">
            <CardContent className="p-4">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Transfers</h2>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : recentTransfers.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">No transfer requests yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {recentTransfers.slice(0, 8).map(transfer => {
                    const createdDate = new Date(transfer.created_at);
                    const firstItemDesc = transfer.items?.[0]?.description || '';
                    return (
                    <div key={transfer.id} className="flex gap-1">
                      {/* Main Transfer Card */}
                      <Card 
                        className={`shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer bg-white border-2 border-blue-200 rounded-xl min-w-0 ${transfer.linked_po_id ? 'flex-[3]' : 'flex-1'}`}
                        onClick={() => setViewingTransfer(transfer)}
                        data-testid={`recent-transfer-card-${transfer.id}`}
                      >
                        <CardContent className="p-4 relative overflow-hidden">
                          {/* Date top-right */}
                          <div className="absolute top-3 right-3 text-right">
                            <div className="text-xs font-semibold text-gray-500 uppercase">
                              {createdDate.toLocaleDateString('en-GB', { weekday: 'short' })}
                            </div>
                            <div className="text-lg font-bold text-gray-700">
                              {createdDate.toLocaleDateString('en-GB', { day: 'numeric' })}
                            </div>
                            <div className="text-xs font-semibold text-gray-500 uppercase">
                              {createdDate.toLocaleDateString('en-GB', { month: 'short' })}
                            </div>
                          </div>
                          <h3 className="font-bold text-base text-gray-900 mb-1 pr-14 truncate">{transfer.branch_name}</h3>
                          {transfer.transfer_ref && (
                            <p className="text-[11px] font-mono font-bold text-blue-600 mb-1 tracking-wide" data-testid={`transfer-ref-card-${transfer.id}`}>
                              {transfer.transfer_ref}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mb-1">
                            {transfer.items?.length || 0} items
                          </p>
                          {firstItemDesc && (
                            <p className="text-xs text-gray-400 truncate mb-2">{firstItemDesc}</p>
                          )}
                          <p className="text-sm text-gray-600">By: {transfer.requested_by}</p>
                        </CardContent>
                      </Card>
                      
                      {/* Linked P.O. Mini Card - Orange */}
                      {transfer.linked_po_id && (
                        <Card 
                          className="flex-1 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer border-2 rounded-xl"
                          style={{ borderColor: enconOrange, backgroundColor: '#fff7ed' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewLinkedPO(transfer.linked_po_id, transfer);
                          }}
                          data-testid={`recent-transfer-po-${transfer.id}`}
                        >
                          <CardContent className="p-2 flex flex-col items-center justify-center h-full">
                            <ClipboardList className="w-4 h-4 mb-1" style={{ color: enconOrange }} />
                            <span className="text-xs font-bold" style={{ color: enconOrange }}>
                              {transfer.linked_po_number || 'P.O.'}
                            </span>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trunking Transfers Summary - Below Recent Transfers */}
          {recentTrunkingTransfers.length > 0 && (
            <Card className="bg-white shadow-md mb-6 rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Truck className="h-5 w-5" style={{ color: enconOrange }} />
                  <h2 className="text-lg font-semibold text-gray-800">Trunking Transfers</h2>
                </div>
                <div className="space-y-3">
                  {recentTrunkingTransfers.map((trunk, idx) => {
                    // Pre-compute: check if ALL dates have passed (entire transfer complete)
                    const now = new Date();
                    now.setHours(0, 0, 0, 0);
                    const allDatesPassed = trunk.dates.filter(Boolean).length > 0 && trunk.dates.filter(Boolean).every(d => {
                      const dObj = new Date(d);
                      dObj.setHours(0, 0, 0, 0);
                      return dObj < now;
                    });
                    
                    return (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.005] transition-all duration-200 cursor-pointer"
                      onClick={() => {
                        if (trunk.first_transfer) {
                          setViewingTrunking(trunk.first_transfer);
                        }
                      }}
                      data-testid={`trunking-transfer-row-${idx}`}
                    >
                      {/* Workflow visualization */}
                      <div className="flex-1 flex items-center gap-2 flex-wrap">
                        {trunk.workflow.map((branch, branchIdx) => {
                          const isLastBranch = branchIdx === trunk.workflow.length - 1;
                          
                          // Each branch's associated date:
                          // branchIdx 0 (origin) -> dates[0] (when stock leaves)
                          // branchIdx N (N>0) -> dates[N-1] (when stock arrives at this branch)
                          const branchDate = branchIdx === 0 ? trunk.dates[0] : trunk.dates[branchIdx - 1];
                          const dateObj = branchDate ? new Date(branchDate) : null;
                          if (dateObj) dateObj.setHours(0, 0, 0, 0);
                          
                          const diffMs = dateObj ? dateObj.getTime() - now.getTime() : null;
                          const diffDays = diffMs !== null ? diffMs / (1000 * 60 * 60 * 24) : null;
                          
                          let colorClass = '';
                          if (isLastBranch) {
                            // End location: grey until complete, then purple
                            if (allDatesPassed) {
                              colorClass = 'bg-purple-200 text-purple-800 border-2 border-purple-500';
                            } else {
                              colorClass = 'bg-gray-200 text-gray-600 border-2 border-gray-400';
                            }
                          } else if (diffDays !== null && diffDays < 0) {
                            // Date has passed - Purple (can't collect anymore)
                            colorClass = 'bg-purple-200 text-purple-800 border-2 border-purple-500';
                          } else if (diffDays !== null && diffDays <= 1) {
                            // Within 1 day before collection - Yellow
                            colorClass = 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400';
                          } else {
                            // More than 1 day away - Green
                            colorClass = 'bg-green-100 text-green-800 border-2 border-green-400';
                          }
                          
                          return (
                            <div key={branchIdx} className="flex items-center gap-2">
                              {branchIdx > 0 && (
                                <ArrowRight className="h-4 w-4 text-gray-400" />
                              )}
                              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${colorClass}`}>
                                <MapPin className="h-3 w-3" />
                                <span>{branch.replace('Encon ', '').replace('Nevill Long ', '')}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Dates */}
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {trunk.dates.filter(Boolean).map(d => formatDate(d)).join(' → ')}
                        </span>
                      </div>
                      
                      {/* Photo thumbnails banner */}
                      {trunk.preview_photos?.length > 0 && (
                        <div 
                          className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); openPhotoModal(trunk.first_transfer?.id); }}
                          data-testid={`trunking-photos-preview-${idx}`}
                        >
                          {trunk.preview_photos.slice(0, 4).map((photo) => (
                            <div key={photo.id} className="w-8 h-8 rounded bg-gray-200 overflow-hidden flex-shrink-0 border border-gray-300">
                              <img src={getPhotoUrl(photo.id)} alt="" className="w-full h-full object-cover" />
                            </div>
                          ))}
                          {trunk.total_photos > 4 && (
                            <span className="text-xs text-gray-500 font-medium">+{trunk.total_photos - 4}</span>
                          )}
                        </div>
                      )}

                      {/* Photo upload button */}
                      <label
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                        onClick={(e) => { 
                          e.stopPropagation();
                          if (trunk.preview_photos?.length > 0) {
                            e.preventDefault();
                            openPhotoModal(trunk.first_transfer?.id);
                          }
                        }}
                        data-testid={`trunking-photo-btn-${idx}`}
                      >
                        <Camera className="h-4 w-4" />
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files?.length && trunk.first_transfer?.id) {
                              handlePhotoUpload(trunk.first_transfer.id, Array.from(e.target.files));
                              e.target.value = '';
                            }
                          }}
                        />
                      </label>

                      {/* Count badge */}
                      {trunk.transfer_count > 1 && (
                        <div className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                          ×{trunk.transfer_count}
                        </div>
                      )}
                      
                      {/* Complete badge */}
                      {allDatesPassed && (
                        <div className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Complete
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Branch Selector */}
          <div className="bg-white shadow-md rounded-xl p-4 md:p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Browse by Branch</h2>
            <BranchSearchInput
              value={browseSearch}
              onChange={setBrowseSearch}
              matches={filterBranches(externalBranches, browseSearch)}
              onPick={(b) => { handleOpenBranchHistory(b); setBrowseSearch(''); }}
              testId="browse-branch-search"
            />
            {browseSearch.trim() && filterBranches(externalBranches, browseSearch).length === 0 && (
              <p className="text-sm text-gray-500 py-4 text-center">No branches match "{browseSearch.trim()}"</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filterBranches(externalBranches, browseSearch).map(branch => (
                <Button
                  key={branch.id}
                  variant="outline"
                  className="h-auto py-3 px-4 text-left justify-start bg-white border-2 rounded-xl shadow-sm hover:shadow-md hover:scale-[1.02] hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 relative"
                  onClick={() => handleOpenBranchHistory(branch)}
                  data-testid={`browse-branch-${branch.id}`}
                >
                  <div className="flex-1">
                    <p className="font-semibold text-sm truncate">{branch.name.replace('Encon ', '').replace('Nevill Long ', '')}</p>
                    <p className="text-xs text-gray-500">{branch.city}</p>
                  </div>
                  {branch.branchCode && (
                    <span className={`absolute top-1 right-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                      branch.branchCode === 'C30' ? 'bg-orange-100 text-orange-600' : 
                      branch.branchCode === 'E70' ? 'bg-purple-100 text-purple-600' : 
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {branch.branchCode}
                    </span>
                  )}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Create Transfer Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open) { if (modalUiV2 && stActivityIdRef.current && !editingTransfer) { activityBar.minimizeActive(); stActivityIdRef.current = null; } setShowCreateModal(false); resetCreateForm(); } }}>
        <DialogContent 
          className="max-w-4xl bg-white"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          {...(modalUiV2 ? { topOffset: activityBar.headerHeight, overlayClassName: 'bg-black/50' } : {})}
          style={{ 
            borderRadius: '24px',
            border: '3px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            // visible (not hidden) so the Recent Orders slide-out can sit outside the modal's right
            // edge; the product area inside still scrolls on its own (overflow-y-auto, flex-1).
            overflowX: 'visible',
            overflowY: 'visible'
          }}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold flex items-center justify-between">
              <span style={{ color: enconOrange }}>
                {editingTransfer ? 'Edit Transfer Request' : 'Create Transfer Request'}
              </span>
              {/* Show linked P.O. badge if exists (both create and edit mode) */}
              {linkedPO && linkedPO.po_number && (
                <div 
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full border-2 cursor-pointer hover:shadow-md transition-all"
                  style={{ borderColor: enconOrange, backgroundColor: '#fff7ed' }}
                  onClick={() => {
                    if (linkedPO.id) {
                      handleEditLinkedPO();
                    } else {
                      handleOpenPOModal();
                    }
                  }}
                >
                  <ClipboardList className="w-4 h-4" style={{ color: enconOrange }} />
                  <span className="font-bold text-sm" style={{ color: enconOrange }}>
                    {linkedPO.po_number}
                  </span>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto overflow-x-hidden space-y-4 py-2">
            {/* Trunking Mode Toggle — sits BESIDE the selected branch's address card once a branch is chosen */}
            <div className={selectedBranch && !isTrunkingMode ? 'grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch' : ''}>
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border-2 border-orange-200">
              <div className="flex items-center gap-2">
                <Truck className="h-5 w-5" style={{ color: enconOrange }} />
                <div>
                  <p className="font-semibold text-sm text-gray-900">Trunking Transfer</p>
                  <p className="text-xs text-gray-500">Multi-branch route with pickups/deliveries</p>
                </div>
              </div>
              <Switch
                checked={isTrunkingMode}
                onCheckedChange={(checked) => {
                  setIsTrunkingMode(checked);
                  if (!checked) {
                    setTrunkingBranches([]);
                  }
                  setSelectedBranch(null);
                }}
              />
            </div>

            {/* Selected branch address — beside the Trunking toggle */}
            {selectedBranch && !isTrunkingMode && (
              <div className="flex items-start justify-between bg-blue-50 rounded-xl p-3 border-2 border-blue-200 min-w-0" data-testid="stock-transfer-selected-branch-card">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <p className="font-bold text-blue-900 text-sm">{selectedBranch.name}</p>
                    {selectedBranch.branchCode && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        selectedBranch.branchCode === 'C30' ? 'bg-orange-100 text-orange-600' : 
                        selectedBranch.branchCode === 'E70' ? 'bg-purple-100 text-purple-600' : 
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {selectedBranch.branchCode}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600">{[selectedBranch.address1, selectedBranch.address2, selectedBranch.city, selectedBranch.postcode].filter(Boolean).join(', ')}</p>
                  {selectedBranch.contact && <p className="text-xs text-gray-600">Contact: {selectedBranch.contact}</p>}
                  {selectedBranch.phone && <p className="text-xs text-gray-600">Tel: {selectedBranch.phone}</p>}
                  {selectedBranch.email && <p className="text-xs text-gray-600 break-all">Email: {selectedBranch.email}</p>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedBranch(null); setShowRecentOrders(false); }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            </div>

            {/* Branch Selection */}
            {!selectedBranch && !isTrunkingMode ? (
              <div>
                <Label className="text-base font-semibold mb-2 block">Select Branch</Label>
                <BranchSearchInput
                  value={branchSearch}
                  onChange={setBranchSearch}
                  matches={filterBranches(externalBranches, branchSearch)}
                  onPick={(b) => { setSelectedBranch(b); setBranchSearch(''); }}
                  testId="create-transfer-branch-search"
                />
                {branchSearch.trim() && filterBranches(externalBranches, branchSearch).length === 0 && (
                  <p className="text-sm text-gray-500 py-4 text-center">No branches match "{branchSearch.trim()}"</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
                  {filterBranches(externalBranches, branchSearch).map(branch => (
                    <Button
                      key={branch.id}
                      variant="outline"
                      className="h-auto py-2 px-2 text-left justify-start border-2 hover:border-blue-500 hover:bg-blue-50 relative"
                      onClick={() => { setSelectedBranch(branch); setBranchSearch(''); }}
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{branch.name}</p>
                        <p className="text-xs text-gray-500">{branch.city}</p>
                      </div>
                      {branch.branchCode && (
                        <span className={`absolute top-1 right-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          branch.branchCode === 'C30' ? 'bg-orange-100 text-orange-600' : 
                          branch.branchCode === 'E70' ? 'bg-purple-100 text-purple-600' : 
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {branch.branchCode}
                        </span>
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            ) : isTrunkingMode ? (
              /* Trunking Mode - Multi-branch selection */
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Select Branches (in order)</Label>
                  {trunkingBranches.length >= 2 && (
                    <Button
                      onClick={openTrunkingSetup}
                      className="text-white"
                      style={{ backgroundColor: enconOrange }}
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Configure Route ({trunkingBranches.length} branches)
                    </Button>
                  )}
                </div>
                
                {/* Selected branches preview */}
                {trunkingBranches.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap mb-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                    {[...trunkingBranches].sort((a, b) => a.order - b.order).map((tb, idx) => (
                      <div key={tb.branch.id} className="flex items-center gap-1">
                        {idx > 0 && <ArrowRight className="h-4 w-4 text-blue-400" />}
                        <div className="flex items-center gap-1 px-2 py-1 bg-white rounded-full border-2 border-blue-300 text-sm font-medium text-blue-800">
                          <span className="w-5 h-5 flex items-center justify-center bg-blue-500 text-white rounded-full text-xs font-bold">
                            {tb.order}
                          </span>
                          <span>{tb.branch.name.replace('Encon ', '').replace('Nevill Long ', '')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <BranchSearchInput
                  value={branchSearch}
                  onChange={setBranchSearch}
                  matches={filterBranches(externalBranches, branchSearch)}
                  onPick={toggleTrunkingBranch}
                  testId="trunking-branch-search"
                />
                {branchSearch.trim() && filterBranches(externalBranches, branchSearch).length === 0 && (
                  <p className="text-sm text-gray-500 py-4 text-center">No branches match "{branchSearch.trim()}"</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[250px] overflow-y-auto">
                  {filterBranches(externalBranches, branchSearch).map(branch => {
                    const orderNum = getBranchOrder(branch.id);
                    const isSelected = orderNum !== null;
                    
                    return (
                      <Button
                        key={branch.id}
                        variant="outline"
                        className={`h-auto py-2 px-2 text-left justify-start border-2 relative transition-all ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'hover:border-orange-400 hover:bg-orange-50'
                        }`}
                        onClick={() => toggleTrunkingBranch(branch)}
                      >
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{branch.name}</p>
                          <p className="text-xs text-gray-500">{branch.city}</p>
                        </div>
                        {isSelected && (
                          <span className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-blue-500 text-white rounded-full text-xs font-bold">
                            {orderNum}
                          </span>
                        )}
                        {branch.branchCode && !isSelected && (
                          <span className={`absolute top-1 right-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            branch.branchCode === 'C30' ? 'bg-orange-100 text-orange-600' : 
                            branch.branchCode === 'E70' ? 'bg-purple-100 text-purple-600' : 
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {branch.branchCode}
                          </span>
                        )}
                      </Button>
                    );
                  })}
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  Click branches in the order of the trunking route. Click again to remove.
                </p>
              </div>
            ) : (
              <>
                {/* Requester Name & Collection Date - Side by Side */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="flex items-center gap-1 text-sm">
                      <User className="h-3 w-3" />
                      Requested By *
                    </Label>
                    <Input
                      value={requestedBy}
                      onChange={(e) => setRequestedBy(e.target.value)}
                      placeholder="Your name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1 text-sm">
                      <Calendar className="h-3 w-3" />
                      Collection Date
                    </Label>
                    <Input
                      type="date"
                      value={collectionDate}
                      onChange={(e) => setCollectionDate(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Products */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-base font-semibold">Products</Label>
                    <button
                      type="button"
                      onClick={() => setShowRecentOrders(v => !v)}
                      className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1 text-xs font-semibold transition-all ${
                        showRecentOrders ? 'text-white' : 'bg-white hover:bg-orange-50'
                      }`}
                      style={showRecentOrders
                        ? { backgroundColor: enconOrange, borderColor: enconOrange }
                        : { borderColor: enconOrange, color: enconOrange }}
                      data-testid="stock-transfer-recent-orders-btn"
                    >
                      <Clock className="h-3.5 w-3.5" />
                      Recent Orders
                    </button>
                  </div>
                  
                  {/* Header row */}
                  <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-500 px-1">
                    <div className="w-28">Product Code</div>
                    <div className="flex-[3]">Description</div>
                    <div className="w-20 text-center">Qty</div>
                    <div className="w-24 text-center">Weight (kg)</div>
                    <div className="w-16"></div>
                  </div>
                  
                  {/* Product lines - min height to prevent jumping when dropdown appears */}
                  <div className="space-y-2 min-h-[120px]">
                    {transferItems.map((item, index) => (
                      <div key={item.id} className="relative" style={{ minHeight: '44px' }}>
                        <div className="flex items-center gap-2">
                          {/* Product Lookup Component - Two separate inputs */}
                          <ProductLookup
                            codeValue={item.product_code}
                            descriptionValue={item.description}
                            onSelect={(product) => handleSelectProduct(index, product)}
                            onCodeChange={(value) => handleItemChange(index, 'product_code', value)}
                            onDescriptionChange={(value) => handleItemChange(index, 'description', value)}
                            showPrice={false}
                            showStock={true}
                            showWeight={true}
                            codeClassName="w-28"
                            descriptionClassName="flex-[3]"
                            codePlaceholder="Code"
                            descriptionPlaceholder="Product description"
                            compareBranchCode={selectedBranch?.branchCode}
                            compareBranchLabel={selectedBranch?.name}
                            data-testid={`stock-transfer-product-lookup-${index}`}
                          />
                          
                          {/* Quantity */}
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="w-20 text-center"
                          />
                          
                          {/* Weight Display */}
                          {(() => {
                            const itemWeight = item.weight || productWeights[item.product_code?.toUpperCase()] || 0;
                            const lineWeight = itemWeight * (parseInt(item.quantity) || 0);
                            return (
                              <div className="w-24 text-center text-sm">
                                {lineWeight > 0 ? (
                                  <span className="text-purple-600 font-medium">{lineWeight.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </div>
                            );
                          })()}
                          
                          {/* Action buttons */}
                          <div className="w-16 flex items-center gap-1">
                            {/* Add button - orange circle with white + */}
                            <button
                              onClick={addNewLine}
                              data-testid="stock-transfer-add-line-btn"
                              className="w-7 h-7 rounded-full flex items-center justify-center text-white"
                              style={{ backgroundColor: enconOrange }}
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                            
                            {/* Remove button - red circle with white X (only show if not first line) */}
                            {index > 0 && (
                              <button
                                onClick={() => removeLine(index)}
                                className="w-7 h-7 rounded-full flex items-center justify-center text-white bg-red-500"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Total Weight Display */}
                  {totalTransferWeight > 0 && (
                    <div className="flex justify-end mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-50 border-2 border-purple-200">
                        <span className="text-sm font-medium text-purple-700">Total Weight (kg):</span>
                        <span className="text-lg font-bold text-purple-600">{totalTransferWeight.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-sm">Notes (optional)</Label>
                  <textarea
                    value={transferNotes}
                    onChange={(e) => setTransferNotes(e.target.value)}
                    placeholder="Any additional notes..."
                    className="mt-1 w-full p-2 border rounded-lg resize-none h-16 text-sm"
                  />
                </div>
                
                {/* Linked P.O. Display - shown when P.O. exists (both create and edit mode) */}
                {linkedPO && (
                  <div 
                    className="p-3 rounded-xl border-2 cursor-pointer hover:shadow-md transition-all"
                    style={{ borderColor: enconOrange, backgroundColor: '#fff7ed' }}
                    onClick={() => {
                      // If we have a saved P.O. id, view it. Otherwise just edit via the P.O. modal
                      if (linkedPO.id) {
                        handleEditLinkedPO();
                      } else {
                        handleOpenPOModal();
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ClipboardList className="w-5 h-5" style={{ color: enconOrange }} />
                        <div>
                          <p className="font-bold" style={{ color: enconOrange }}>{linkedPO.po_number || 'New P.O.'}</p>
                          <p className="text-xs text-gray-600">
                            {linkedPO.supplier || 'No supplier'} - £{(linkedPO.total_cost || 0).toFixed(2)}
                            {linkedPO.customer_recharge_amount > 0 && ` | Cust: £${linkedPO.customer_recharge_amount.toFixed(2)}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-xs" style={{ color: enconOrange }}>
                        <Edit className="w-3 h-3" />
                        <span>Edit P.O.</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* External Transport P.O. Option - only when no P.O. linked */}
                {!linkedPO && (
                  <button
                    type="button"
                    onClick={handleOpenPOModal}
                    className="w-full p-3 rounded-xl border-2 border-dashed hover:border-solid transition-all flex items-center justify-center gap-2"
                    style={{ borderColor: enconOrange, color: enconOrange }}
                  >
                    <ClipboardList className="w-4 h-4" />
                    <span className="font-semibold text-sm">Add External Transport P.O.</span>
                  </button>
                )}
              </>
            )}
          </div>

          {/* Actions - Fixed at bottom of modal */}
          {selectedBranch && (
            <div className="flex-shrink-0 flex gap-3 pt-3 border-t bg-white">
              <Button
                variant="outline"
                className="flex-1 py-4 rounded-full border-2"
                onClick={() => { if (modalUiV2 && stActivityIdRef.current && !editingTransfer) { activityBar.minimizeActive(); stActivityIdRef.current = null; } setShowCreateModal(false); resetCreateForm(); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 py-4 rounded-full text-white font-semibold"
                style={{ background: 'linear-gradient(135deg, #ee7203, #ec4899)' }}
                onClick={handleSaveTransfer}
                disabled={savingTransfer}
              >
                {savingTransfer ? 'Saving...' : (editingTransfer ? 'Update Transfer' : 'Create Transfer')}
              </Button>
            </div>
          )}

          {/* Recent Orders slide-out — rendered INSIDE the dialog content so focus/pointer handling
              treats it as part of the modal */}
          {showRecentOrders && selectedBranch && !isTrunkingMode && (
            <RecentOrdersPanel
              onClose={() => setShowRecentOrders(false)}
              onAddLine={handleAddOrderLine}
              existingCodes={new Set(transferItems.map(i => String(i.product_code || '').toUpperCase()).filter(Boolean))}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Branch History Modal */}
      <Dialog open={showBranchHistoryModal} onOpenChange={setShowBranchHistoryModal}>
        <DialogContent className="max-w-4xl bg-white" style={{ borderRadius: '24px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedHistoryBranch?.name} - Transfer History
            </DialogTitle>
          </DialogHeader>
          
          {/* Create Transfer Button - below title, white with orange border */}
          <div className="flex justify-end -mt-2 mb-2">
            <Button
              onClick={handleCreateTransferForBranch}
              variant="outline"
              className="flex items-center gap-2 font-semibold rounded-xl px-4 py-2 border-2"
              style={{ borderColor: enconOrange, color: enconOrange, backgroundColor: 'white' }}
              data-testid="branch-history-create-transfer"
            >
              <Plus className="h-4 w-4" />
              Create Transfer
            </Button>
          </div>

          {/* Month/Year Selector */}
          <div className="flex items-center justify-center gap-4 py-3 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => changeHistoryMonth(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-semibold text-lg min-w-[180px] text-center">
              {months[historyMonth]} {historyYear}
            </span>
            <Button variant="outline" size="sm" onClick={() => changeHistoryMonth(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Transfers Grid - 3 columns of square cards */}
            {loadingHistory ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : branchTransfers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No transfers for this month</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {branchTransfers.map(transfer => {
                  const transferDate = new Date(transfer.created_at);
                  const firstItemDesc = transfer.items?.[0]?.description || '';
                  return (
                    <Card
                      key={transfer.id}
                      className="shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer border-2 border-blue-100 rounded-xl aspect-square flex flex-col"
                      onClick={() => { setViewingTransfer(transfer); setShowBranchHistoryModal(false); }}
                      data-testid={`branch-history-card-${transfer.id}`}
                    >
                      <CardContent className="p-4 flex flex-col flex-1 relative">
                        {/* Date top-right */}
                        <div className="absolute top-3 right-3 text-right">
                          <div className="text-xs font-semibold text-gray-500 uppercase">
                            {transferDate.toLocaleDateString('en-GB', { weekday: 'short' })}
                          </div>
                          <div className="text-lg font-bold text-gray-700">
                            {transferDate.toLocaleDateString('en-GB', { day: 'numeric' })}
                          </div>
                          <div className="text-xs font-semibold text-gray-500 uppercase">
                            {transferDate.toLocaleDateString('en-GB', { month: 'short' })}
                          </div>
                        </div>
                        
                        {/* Items count */}
                        <div className="flex items-center gap-1 mb-2">
                          <Package className="h-4 w-4 text-blue-500" />
                          <span className="text-sm font-semibold text-blue-700">{transfer.items?.length || 0} items</span>
                        </div>
                        
                        {/* First item description */}
                        {firstItemDesc && (
                          <p className="text-xs text-gray-400 truncate pr-14 mb-auto">{firstItemDesc}</p>
                        )}
                        
                        {/* Bottom: requester/receiver */}
                        <div className="mt-auto pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-1.5">
                            <User className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-600">Requested by: <strong>{transfer.requested_by}</strong></span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
            
            {/* Trunking Transfers involving this branch - Table at bottom */}
            {selectedHistoryBranch && recentTrunkingTransfers.length > 0 && (() => {
              // Filter trunking transfers that involve the selected branch
              const branchName = selectedHistoryBranch.name;
              const relevantTrunking = recentTrunkingTransfers.filter(trunk => 
                trunk.workflow.some(w => w === branchName || 
                  w.replace('Encon ', '').replace('Nevill Long ', '') === branchName.replace('Encon ', '').replace('Nevill Long ', ''))
              );
              
              if (relevantTrunking.length === 0) return null;
              
              const now = new Date();
              now.setHours(0, 0, 0, 0);
              
              return (
                <div className="mt-4 pt-4 border-t-2 border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Truck className="h-4 w-4" style={{ color: enconOrange }} />
                    <span className="font-semibold text-sm text-gray-800">Trunking Transfers involving {selectedHistoryBranch.name.replace('Encon ', '').replace('Nevill Long ', '')}</span>
                  </div>
                  <div className="space-y-2">
                    {relevantTrunking.map((trunk, tIdx) => {
                      const allDatesPassed = trunk.dates.filter(Boolean).length > 0 && trunk.dates.filter(Boolean).every(d => {
                        const dObj = new Date(d);
                        dObj.setHours(0, 0, 0, 0);
                        return dObj < now;
                      });
                      
                      return (
                        <div 
                          key={tIdx}
                          className="flex items-center gap-2 p-2 bg-orange-50 rounded-lg border border-orange-200 cursor-pointer hover:shadow-md transition-all"
                          onClick={() => {
                            if (trunk.first_transfer) {
                              setShowBranchHistoryModal(false);
                              setViewingTrunking(trunk.first_transfer);
                            }
                          }}
                        >
                          <div className="flex items-center gap-1.5 flex-wrap flex-1">
                            {trunk.workflow.map((branch, bIdx) => {
                              const isLast = bIdx === trunk.workflow.length - 1;
                              const bDate = bIdx === 0 ? trunk.dates[0] : trunk.dates[bIdx - 1];
                              const dateObj = bDate ? new Date(bDate) : null;
                              if (dateObj) dateObj.setHours(0, 0, 0, 0);
                              const diffMs = dateObj ? dateObj.getTime() - now.getTime() : null;
                              const diffDays = diffMs !== null ? diffMs / (1000 * 60 * 60 * 24) : null;
                              
                              let pillColor = '';
                              if (isLast) {
                                pillColor = allDatesPassed ? 'bg-purple-200 text-purple-800' : 'bg-gray-200 text-gray-600';
                              } else if (diffDays !== null && diffDays < 0) {
                                pillColor = 'bg-purple-200 text-purple-800';
                              } else if (diffDays !== null && diffDays <= 1) {
                                pillColor = 'bg-yellow-100 text-yellow-800';
                              } else {
                                pillColor = 'bg-green-100 text-green-800';
                              }
                              
                              return (
                                <span key={bIdx} className="flex items-center gap-1">
                                  {bIdx > 0 && <ArrowRight className="h-3 w-3 text-gray-400" />}
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${pillColor}`}>
                                    {branch.replace('Encon ', '').replace('Nevill Long ', '')}
                                  </span>
                                </span>
                              );
                            })}
                          </div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {trunk.dates.filter(Boolean).map(d => formatDate(d)).join(' → ')}
                          </span>
                          {allDatesPassed && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px] font-bold">Complete</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* View Transfer Modal */}
      <Dialog open={!!viewingTransfer && !showCreateModal} onOpenChange={(open) => { if (!open) setViewingTransfer(null); }}>
        <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" style={{ borderRadius: '24px' }} onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} {...(modalUiV2 ? { topOffset: activityBar.headerHeight, overlayClassName: 'bg-black/50' } : {})}>
          {viewingTransfer && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="h-6 w-6 text-blue-600" />
                    Transfer Request
                    {viewingTransfer.transfer_ref && (
                      <span className="ml-1 px-2.5 py-0.5 text-xs font-mono font-bold bg-blue-600 text-white rounded-md tracking-wide" data-testid="transfer-ref-view">
                        {viewingTransfer.transfer_ref}
                      </span>
                    )}
                  </div>
                  {/* Linked P.O. Badge - Same line as title, smaller */}
                  {viewingTransfer.linked_po_id && (
                    <div 
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full border-2"
                      style={{ borderColor: enconOrange, backgroundColor: '#fff7ed' }}
                    >
                      <ClipboardList className="w-4 h-4" style={{ color: enconOrange }} />
                      <span className="font-bold text-sm" style={{ color: enconOrange }}>
                        {viewingTransfer.linked_po_number || 'P.O.'}
                      </span>
                    </div>
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {/* Branch Info */}
                <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                  <p className="font-bold text-blue-900 text-lg">{viewingTransfer.branch_name}</p>
                  <p className="text-sm text-gray-600">{viewingTransfer.branch_address}</p>
                  <p className="text-sm text-gray-600 mt-2">
                    <strong>Requested By:</strong> {viewingTransfer.requested_by}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Date:</strong> {new Date(viewingTransfer.created_at).toLocaleDateString('en-GB')}
                  </p>
                  {viewingTransfer.collection_date && (
                    <p className="text-sm text-gray-600">
                      <strong>Collection:</strong> {new Date(viewingTransfer.collection_date).toLocaleDateString('en-GB')}
                    </p>
                  )}
                </div>

                {/* Items */}
                <div>
                  <h3 className="font-semibold mb-2">Products ({viewingTransfer.items?.length || 0})</h3>
                  <div className="border rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left p-2">Code</th>
                          <th className="text-left p-2">Description</th>
                          <th className="text-right p-2">Qty</th>
                          <th className="text-right p-2">Weight (kg)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(viewingTransfer.items || []).map((item, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2 font-mono text-xs">{item.product_code}</td>
                            <td className="p-2">{item.description}</td>
                            <td className="p-2 text-right font-semibold">{item.quantity_required} {item.stock_unit}</td>
                            <td className="p-2 text-right text-purple-600 font-medium">
                              {item.line_weight > 0 ? item.line_weight.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Total Weight Display in View Modal */}
                  {viewingTransfer.total_weight > 0 && (
                    <div className="flex justify-end mt-3">
                      <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-50 border-2 border-purple-200">
                        <span className="text-sm font-medium text-purple-700">Total Weight (kg):</span>
                        <span className="text-lg font-bold text-purple-600">{viewingTransfer.total_weight.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes - show P.O. number at start if linked */}
                {(viewingTransfer.notes || viewingTransfer.linked_po_number) && (
                  <div className="bg-gray-50 p-3 rounded-xl">
                    <p className="text-sm text-gray-600">
                      <strong>Notes:</strong>{' '}
                      {viewingTransfer.linked_po_number && (
                        <span className="font-semibold" style={{ color: enconOrange }}>
                          {viewingTransfer.linked_po_number}
                        </span>
                      )}
                      {viewingTransfer.linked_po_number && viewingTransfer.notes && ' - '}
                      {viewingTransfer.notes}
                    </p>
                  </div>
                )}
                
                {/* Linked P.O. Details Card - Clickable to edit P.O. */}
                {viewingTransfer.linked_po_id && (
                  <div 
                    className="p-4 rounded-xl border-2 cursor-pointer hover:shadow-lg transition-all"
                    style={{ borderColor: enconOrange, backgroundColor: '#fff7ed' }}
                    onClick={() => handleViewLinkedPO(viewingTransfer.linked_po_id, viewingTransfer)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <ClipboardList className="w-5 h-5" style={{ color: enconOrange }} />
                        <span className="font-bold" style={{ color: enconOrange }}>
                          Linked P.O. - {viewingTransfer.linked_po_number}
                        </span>
                      </div>
                      <Edit className="w-4 h-4" style={{ color: enconOrange }} />
                    </div>
                    <p className="text-sm text-gray-600">
                      Click to view and edit P.O. details
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-4 sticky bottom-0 bg-white pb-2">
                  <Button
                    variant="outline"
                    className="flex-1 min-w-[100px] py-4 rounded-xl border-2"
                    style={{ borderColor: enconOrange, color: enconOrange, backgroundColor: 'white' }}
                    onClick={handleEditTransfer}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    className="py-4 px-4 rounded-xl border-2"
                    style={{ borderColor: enconOrange, color: enconOrange, backgroundColor: 'white' }}
                    onClick={() => loadPdfPreview(viewingTransfer)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    className="py-4 px-4 rounded-xl border-2 border-red-400 text-red-600 hover:bg-red-50"
                    onClick={handleDeleteTransfer}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    className="py-4 px-4 rounded-xl border-2"
                    style={{ borderColor: enconOrange, color: enconOrange, backgroundColor: 'white' }}
                    onClick={() => printTransfer(viewingTransfer)}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                  <Button
                    className="flex-1 min-w-[140px] py-4 rounded-xl text-white font-semibold"
                    style={{ background: 'linear-gradient(135deg, #ee7203, #ec4899)' }}
                    onClick={handleSendTransfer}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send Transfer
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Upcoming Transfer Detail Modal */}
      <Dialog open={!!selectedUpcoming} onOpenChange={(open) => { if (!open) setSelectedUpcoming(null); }}>
        <DialogContent className="max-w-md bg-white" style={{ borderRadius: '24px' }} onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          {selectedUpcoming && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-bold flex items-center gap-2">
                  <Truck className="h-5 w-5 text-blue-600" />
                  Upcoming Transfer on Own Vehicle
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className={`p-4 rounded-xl border-2 ${
                  new Date(selectedUpcoming.date).toDateString() === new Date().toDateString()
                    ? 'border-yellow-400 bg-yellow-50'
                    : 'border-green-400 bg-green-50'
                }`}>
                  <p className="font-bold text-lg text-gray-900">{selectedUpcoming.branch_name}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(selectedUpcoming.date).toLocaleDateString('en-GB', { 
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
                    })}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600 uppercase font-semibold">Vehicle</p>
                    <p className="font-bold text-blue-900">{selectedUpcoming.vehicle_registration || selectedUpcoming.vehicle || 'N/A'}</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600 uppercase font-semibold">Run</p>
                    <p className="font-bold text-blue-900">{selectedUpcoming.run_number || 'Main'}</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Postcode</p>
                  <p className="font-semibold text-gray-900">{selectedUpcoming.postcode || 'N/A'}</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 py-5 rounded-xl border-2"
                    onClick={() => setSelectedUpcoming(null)}
                  >
                    Close
                  </Button>
                  <Button
                    className="flex-1 py-5 rounded-xl text-white font-semibold"
                    style={{ backgroundColor: enconOrange }}
                    onClick={() => {
                      setSelectedUpcoming(null);
                      navigateToTransportDate(selectedUpcoming.date);
                    }}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View in Transport
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <Dialog open={!!pdfPreviewTransfer} onOpenChange={(open) => { 
        if (!open) {
          setPdfPreviewTransfer(null);
          if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
          }
        }
      }}>
        <DialogContent className="max-w-4xl h-[90vh] bg-white" style={{ borderRadius: '24px' }} onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Eye className="h-5 w-5" style={{ color: enconOrange }} />
              Transfer Document Preview
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full min-h-[500px]">
            {loadingPdf ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">Loading PDF...</p>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                className="w-full h-full border-0 rounded-lg"
                title="Transfer PDF Preview"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">Failed to load PDF</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* P.O. Expense Modal (overlays on top of Create Transfer Modal) */}
      <Dialog open={showPOModal} onOpenChange={setShowPOModal}>
        <DialogContent 
          className="max-w-lg bg-white z-[60]"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          style={{ 
            borderRadius: '24px',
            border: '3px solid',
            borderColor: enconOrange,
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ color: enconOrange }}>
              <ClipboardList className="w-5 h-5" />
              {linkedPO ? 'Edit P.O. Expense' : 'Add External Transport P.O.'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* P.O. Type at top */}
            <div>
              <Label className="text-sm font-semibold">P.O. Type</Label>
              <select
                className="w-full p-2.5 border rounded-lg text-sm mt-1"
                value={poFormData.po_type}
                onChange={(e) => setPOFormData({ ...poFormData, po_type: e.target.value })}
                style={{ borderColor: enconOrange }}
              >
                {PO_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            {/* Main Form Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Supplier</Label>
                <Input
                  value={poFormData.supplier}
                  onChange={(e) => setPOFormData({ ...poFormData, supplier: e.target.value })}
                  placeholder="Supplier name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Date</Label>
                <Input
                  type="date"
                  value={poFormData.order_date}
                  onChange={(e) => setPOFormData({ ...poFormData, order_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Ordered By</Label>
                <Input
                  value={poFormData.ordered_by}
                  onChange={(e) => setPOFormData({ ...poFormData, ordered_by: e.target.value })}
                  placeholder="Initials"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">M4 Order No</Label>
                <Input
                  value={poFormData.m4_order_no}
                  onChange={(e) => setPOFormData({ ...poFormData, m4_order_no: e.target.value })}
                  placeholder="Order number"
                  className="mt-1"
                />
              </div>
            </div>
            
            {/* Cost Boxes */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg border-2" style={{ borderColor: enconOrange, backgroundColor: '#fff7ed' }}>
                <Label className="text-xs font-semibold" style={{ color: enconOrange }}>Cost (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={poFormData.total_cost || ''}
                  onChange={(e) => setPOFormData({ ...poFormData, total_cost: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="text-lg font-bold mt-1"
                  style={{ borderColor: enconOrange }}
                />
              </div>
              <div className="p-3 rounded-lg border-2 border-blue-400 bg-blue-50">
                <Label className="text-xs font-semibold text-blue-600">Customer Rech. (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={poFormData.customer_recharge_amount || ''}
                  onChange={(e) => setPOFormData({ ...poFormData, customer_recharge_amount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="text-lg font-bold mt-1 border-blue-400"
                />
              </div>
              <div className="p-3 rounded-lg border-2 border-purple-400 bg-purple-50">
                <Label className="text-xs font-semibold text-purple-600">H/O Rech. (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={poFormData.ho_recharge_amount || ''}
                  onChange={(e) => setPOFormData({ ...poFormData, ho_recharge_amount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="text-lg font-bold mt-1 border-purple-400"
                />
              </div>
            </div>
            
            {/* Notes */}
            <div>
              <Label className="text-sm">Notes</Label>
              <textarea
                className="w-full p-2 border rounded-lg resize-none mt-1"
                rows={2}
                value={poFormData.notes}
                onChange={(e) => setPOFormData({ ...poFormData, notes: e.target.value })}
                placeholder="Additional notes"
              />
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1 py-4 rounded-full border-2"
              onClick={() => setShowPOModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 py-4 rounded-full text-white font-semibold"
              style={{ backgroundColor: enconOrange }}
              onClick={handleSavePO}
            >
              {linkedPO ? 'Update P.O.' : 'Create P.O.'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* View Linked P.O. Modal */}
      <Dialog open={showLinkedPOModal} onOpenChange={setShowLinkedPOModal}>
        <DialogContent 
          className="max-w-2xl"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          style={{
            background: 'white',
            border: '3px solid',
            borderColor: enconOrange,
            borderRadius: '24px'
          }}
        >
          {viewingLinkedPO && (
            <>
              {/* P.O. Number Badge - Top Center */}
              <div className="flex justify-center -mt-2 mb-2">
                <div 
                  className="flex items-center gap-2 px-4 py-2 rounded-full border-2"
                  style={{ borderColor: enconOrange, backgroundColor: '#fff7ed' }}
                >
                  <ClipboardList className="w-5 h-5" style={{ color: enconOrange }} />
                  <span className="font-bold text-base" style={{ color: enconOrange }}>
                    {viewingLinkedPO.po_number}
                  </span>
                  {viewingLinkedPO.status === 'cancelled' && (
                    <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded-full">Cancelled</span>
                  )}
                </div>
              </div>
              
              {/* Linked Transfer Badge */}
              {linkedTransferForPO && (
                <div className="flex justify-center mb-2">
                  <button
                    onClick={handleEditLinkedTransferFromPO}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full border-2 border-blue-400 bg-blue-50 hover:shadow-md transition-all"
                  >
                    <Package className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-sm text-blue-600">
                      {linkedTransferForPO.branch_name} Transfer
                    </span>
                  </button>
                </div>
              )}
              
              <DialogHeader>
                <DialogTitle className="text-xl font-bold" style={{ color: enconOrange }}>
                  {editingLinkedPO ? 'Edit P.O.' : 'Linked P.O.'}
                </DialogTitle>
              </DialogHeader>
              
              {!editingLinkedPO ? (
                <div className="space-y-4">
                  {/* Summary Info */}
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl">
                    <div>
                      <div className="text-xs text-gray-500">Supplier</div>
                      <div className="font-medium">{viewingLinkedPO.supplier || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Date</div>
                      <div className="font-medium">{viewingLinkedPO.order_date}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Ordered By</div>
                      <div className="font-medium">{viewingLinkedPO.ordered_by || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Type</div>
                      <div className="font-medium">
                        {PO_TYPES.find(t => t.value === viewingLinkedPO.po_type)?.label || viewingLinkedPO.po_type}
                      </div>
                    </div>
                    {viewingLinkedPO.m4_order_no && (
                      <div>
                        <div className="text-xs text-gray-500">M4 Order No</div>
                        <div className="font-medium">{viewingLinkedPO.m4_order_no}</div>
                      </div>
                    )}
                  </div>
                  
                  {/* Cost Boxes */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-orange-50 p-3 rounded-xl border-2 text-center" style={{ borderColor: enconOrange }}>
                      <div className="text-xs text-gray-500">Cost</div>
                      <div className="font-bold text-xl" style={{ color: enconOrange }}>
                        £{(viewingLinkedPO.total_cost || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-xl border-2 border-blue-400 text-center">
                      <div className="text-xs text-gray-500">Cust. Rech.</div>
                      <div className="font-bold text-xl text-blue-600">
                        £{(viewingLinkedPO.customer_recharge_amount || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-xl border-2 border-purple-400 text-center">
                      <div className="text-xs text-gray-500">H/O Rech.</div>
                      <div className="font-bold text-xl text-purple-600">
                        £{(viewingLinkedPO.ho_recharge_amount || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Notes - show linked transfer info */}
                  <div className="bg-gray-50 p-3 rounded-xl">
                    <div className="text-xs text-gray-500 mb-1">Notes</div>
                    <div className="text-sm">
                      {linkedTransferForPO && (
                        <span className="font-semibold text-blue-600">
                          {linkedTransferForPO.branch_name?.replace('Encon ', '').replace('Nevill Long ', '')} Transfer
                        </span>
                      )}
                      {linkedTransferForPO && viewingLinkedPO.notes && ' - '}
                      {viewingLinkedPO.notes || (!linkedTransferForPO && '-')}
                    </div>
                  </div>
                </div>
              ) : (
                /* Edit Mode Form */
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Supplier</Label>
                      <Input
                        value={editPOData?.supplier || ''}
                        onChange={(e) => setEditPOData({...editPOData, supplier: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={editPOData?.order_date || ''}
                        onChange={(e) => setEditPOData({...editPOData, order_date: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Ordered By</Label>
                      <Input
                        value={editPOData?.ordered_by || ''}
                        onChange={(e) => setEditPOData({...editPOData, ordered_by: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>M4 Order No</Label>
                      <Input
                        value={editPOData?.m4_order_no || ''}
                        onChange={(e) => setEditPOData({...editPOData, m4_order_no: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  {/* Cost Fields */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Cost (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editPOData?.total_cost || 0}
                        onChange={(e) => setEditPOData({...editPOData, total_cost: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Cust. Rech. (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editPOData?.customer_recharge_amount || 0}
                        onChange={(e) => setEditPOData({...editPOData, customer_recharge_amount: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>H/O Rech. (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editPOData?.ho_recharge_amount || 0}
                        onChange={(e) => setEditPOData({...editPOData, ho_recharge_amount: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Notes</Label>
                    <textarea
                      value={editPOData?.notes || ''}
                      onChange={(e) => setEditPOData({...editPOData, notes: e.target.value})}
                      placeholder="Additional notes..."
                      className="mt-1 w-full p-3 border rounded-xl resize-none h-16"
                    />
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t mt-4 sticky bottom-0 bg-white pb-2">
                {!editingLinkedPO ? (
                  <>
                    <Button
                      variant="outline"
                      className="flex-1 py-4 rounded-xl border-2"
                      onClick={() => {
                        setShowLinkedPOModal(false);
                        setViewingLinkedPO(null);
                        setLinkedTransferForPO(null);
                      }}
                    >
                      Close
                    </Button>
                    {linkedTransferForPO && (
                      <Button
                        variant="outline"
                        className="flex-1 py-4 rounded-xl border-2 border-blue-500 text-blue-600"
                        onClick={handleEditLinkedTransferFromPO}
                      >
                        <Package className="h-4 w-4 mr-2" />
                        Edit Transfer
                      </Button>
                    )}
                    <Button
                      className="flex-1 py-4 rounded-xl text-white font-semibold"
                      style={{ backgroundColor: enconOrange }}
                      onClick={handleStartEditPO}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit P.O.
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className="flex-1 py-4 rounded-xl border-2"
                      onClick={() => {
                        setEditingLinkedPO(false);
                        setEditPOData(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 py-4 rounded-xl text-white font-semibold"
                      style={{ backgroundColor: enconOrange }}
                      onClick={handleSavePOEdit}
                      disabled={savingPOEdit}
                    >
                      {savingPOEdit ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add to Goods In Dialog */}
      <Dialog open={showAddToGoodsInDialog} onOpenChange={setShowAddToGoodsInDialog}>
        <DialogContent className="bg-white max-w-md mx-4 rounded-xl shadow-2xl" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Package className="h-6 w-6 text-blue-600" />
              Add to Goods In?
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              This transfer is coming in from <span className="font-semibold">{pendingBranchName}</span>
            </p>
            
            <p className="text-sm text-gray-500 mb-4">
              Add to {effectiveBranchId} Goods In?
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 py-3"
                onClick={() => {
                  setShowAddToGoodsInDialog(false);
                  setPendingTransferId(null);
                  setPendingBranchName(null);
                }}
              >
                No Thanks
              </Button>
              <Button
                className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 text-white"
                onClick={() => handleAddToGoodsIn(effectiveBranchId)}
              >
                Yes, Add to Goods In
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trunking Setup Modal */}
      <Dialog open={showTrunkingSetupModal} onOpenChange={(open) => {
        if (!open) {
          setShowTrunkingSetupModal(false);
        }
      }}>
        <DialogContent 
          className="max-w-6xl bg-white"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          style={{ 
            borderRadius: '24px',
            border: '3px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #f59e0b)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            width: '95vw'
          }}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Truck className="h-6 w-6" style={{ color: enconOrange }} />
              <span style={{ color: enconOrange }}>Configure Trunking Transfer</span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-2">
            {/* Transfer Name */}
            <div>
              <Label className="text-sm font-medium">Transfer Name (optional)</Label>
              <Input
                value={trunkingName}
                onChange={(e) => setTrunkingName(e.target.value)}
                placeholder={`Trunk-${new Date().toISOString().slice(0, 10)}`}
                className="mt-1"
              />
            </div>

            {/* Workflow Preview */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
              <div className="flex items-center gap-2 flex-wrap justify-center">
                {trunkingLegs.map((leg, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    {idx === 0 && (
                      <div className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold">
                        {leg.from_branch_name.replace('Encon ', '').replace('Nevill Long ', '')}
                      </div>
                    )}
                    <div className="flex flex-col items-center">
                      <ArrowRight className="h-5 w-5 text-gray-400" />
                      <span className="text-[10px] text-gray-500">Leg {leg.leg_number}</span>
                    </div>
                    <div className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold">
                      {leg.to_branch_name.replace('Encon ', '').replace('Nevill Long ', '')}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Leg Configuration */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Configure Each Leg</Label>
              
              {trunkingLegs.map((leg, idx) => (
                <Card key={idx} className="border-2 border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-7 h-7 flex items-center justify-center bg-blue-500 text-white rounded-full text-sm font-bold">
                        {leg.leg_number}
                      </span>
                      <span className="font-semibold text-gray-900">
                        {leg.from_branch_name.replace('Encon ', '').replace('Nevill Long ', '')}
                      </span>
                      <ArrowRight className="h-4 w-4 text-gray-400" />
                      <span className="font-semibold text-gray-900">
                        {leg.to_branch_name.replace('Encon ', '').replace('Nevill Long ', '')}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                      {/* Action Type */}
                      <div className="md:col-span-2">
                        <Label className="text-xs text-gray-500">Action Type</Label>
                        <div className="flex gap-2 mt-1">
                          <button
                            type="button"
                            onClick={() => updateTrunkingLeg(idx, 'action_type', 'collect')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              leg.action_type === 'collect'
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {leg.to_branch_name.split(' ')[1] || 'Dest'} Collects
                          </button>
                          <button
                            type="button"
                            onClick={() => updateTrunkingLeg(idx, 'action_type', 'deliver')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              leg.action_type === 'deliver'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {leg.from_branch_name.split(' ')[1] || 'Origin'} Delivers
                          </button>
                        </div>
                      </div>

                      {/* Date */}
                      <div>
                        <Label className="text-xs text-gray-500">Scheduled Date</Label>
                        <Input
                          type="date"
                          value={leg.scheduled_date}
                          onChange={(e) => updateTrunkingLeg(idx, 'scheduled_date', e.target.value)}
                          className="mt-1"
                        />
                      </div>

                      {/* Sent By (auto-filled, read-only display) */}
                      <div>
                        <Label className="text-xs text-gray-500">Sent By</Label>
                        <div className="mt-1 px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-700 font-medium flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          {leg.action_by || getUserDisplayName() || 'Unknown'}
                        </div>
                      </div>
                    </div>

                    {/* Products for this leg */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      {/* Show carried-over products from previous legs (read-only) */}
                      {idx > 0 && (() => {
                        // Collect all items from previous legs
                        const carriedItems = [];
                        for (let i = 0; i < idx; i++) {
                          const prevLeg = trunkingLegs[i];
                          if (prevLeg.items && prevLeg.items.length > 0) {
                            prevLeg.items.forEach(item => {
                              if (item.product_code) {
                                carriedItems.push({ ...item, fromLeg: i + 1 });
                              }
                            });
                          }
                        }
                        
                        if (carriedItems.length > 0) {
                          return (
                            <div className="mb-3">
                              <Label className="text-xs text-gray-500 mb-2 block">
                                Products carried from previous legs
                              </Label>
                              <div className="space-y-1">
                                {carriedItems.map((item, i) => (
                                  <div key={i} className="flex items-center gap-2 bg-blue-50 border border-blue-200 p-2 rounded-lg text-sm">
                                    <span className="w-5 h-5 flex items-center justify-center bg-blue-500 text-white rounded-full text-[10px] font-bold">
                                      {item.fromLeg}
                                    </span>
                                    <span className="font-mono text-blue-800 w-24">{item.product_code}</span>
                                    <span className="flex-1 text-gray-700 truncate">{item.description}</span>
                                    <span className="font-semibold text-blue-600 w-16 text-center">×{item.quantity}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      
                      {/* Add new products for this leg */}
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs text-gray-500">
                          {idx === 0 ? 'Products (will carry through all legs)' : 'Additional Products (added at this leg)'}
                        </Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCurrentLegIndex(idx);
                            addItemToLeg(idx, { product_code: '', description: '', quantity: 1, unit_weight: 0 });
                          }}
                          className="text-xs"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Product
                        </Button>
                      </div>
                      
                      {leg.items && leg.items.length > 0 ? (
                        <div className="space-y-2">
                          {leg.items.map((item, itemIdx) => (
                            <div key={itemIdx} className="flex items-center gap-3 bg-gray-50 p-2 rounded-lg">
                              <ProductLookup
                                codeValue={item.product_code}
                                descriptionValue={item.description}
                                onSelect={(product) => {
                                  const updated = [...trunkingLegs];
                                  updated[idx].items[itemIdx] = {
                                    ...updated[idx].items[itemIdx],
                                    product_code: product.product_code || product.code || '',
                                    description: product.description || '',
                                    unit_weight: product.weight || product.unit_weight || 0
                                  };
                                  setTrunkingLegs(updated);
                                }}
                                onCodeChange={(value) => {
                                  const updated = [...trunkingLegs];
                                  updated[idx].items[itemIdx].product_code = value;
                                  setTrunkingLegs(updated);
                                }}
                                onDescriptionChange={(value) => {
                                  const updated = [...trunkingLegs];
                                  updated[idx].items[itemIdx].description = value;
                                  setTrunkingLegs(updated);
                                }}
                                showPrice={false}
                                showStock={true}
                                showWeight={true}
                                codeClassName="w-40"
                                descriptionClassName="flex-1 min-w-[300px]"
                                codePlaceholder="Product Code"
                                descriptionPlaceholder="Search product by description..."
                              />
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => {
                                  const updated = [...trunkingLegs];
                                  updated[idx].items[itemIdx].quantity = parseInt(e.target.value) || 1;
                                  setTrunkingLegs(updated);
                                }}
                                placeholder="Qty"
                                className="w-24 text-center"
                                min="1"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItemFromLeg(idx, itemIdx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        idx === 0 ? (
                          <p className="text-xs text-gray-400 italic">No products added yet - add at least one product</p>
                        ) : (
                          <p className="text-xs text-gray-400 italic">No additional products for this leg (using carried items only)</p>
                        )
                      )}
                    </div>

                    {/* Notes */}
                    <div className="mt-3">
                      <Label className="text-xs text-gray-500">Notes for this leg</Label>
                      <Input
                        value={leg.notes}
                        onChange={(e) => updateTrunkingLeg(idx, 'notes', e.target.value)}
                        placeholder="Optional notes..."
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* General Notes */}
            <div>
              <Label className="text-sm font-medium">General Notes</Label>
              <Input
                value={transferNotes}
                onChange={(e) => setTransferNotes(e.target.value)}
                placeholder="Any additional notes for the entire transfer..."
                className="mt-1"
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 flex-shrink-0">
            <Button
              variant="outline"
              className="flex-1 py-3 rounded-xl border-2"
              onClick={() => {
                setShowTrunkingSetupModal(false);
                if (!editingTrunkingId) {
                  setShowCreateModal(true);
                }
                resetTrunkingForm();
              }}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              {editingTrunkingId ? 'Cancel' : 'Back'}
            </Button>
            <Button
              className="flex-1 py-3 rounded-xl text-white font-semibold"
              style={{ backgroundColor: enconOrange }}
              onClick={saveTrunkingTransfer}
              disabled={savingTrunking}
            >
              {savingTrunking ? (editingTrunkingId ? 'Updating...' : 'Creating...') : (editingTrunkingId ? 'Update Trunking Transfer' : 'Create Trunking Transfer')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Trunking Transfer Modal */}
      <Dialog open={!!viewingTrunking} onOpenChange={(open) => !open && setViewingTrunking(null)}>
        <DialogContent 
          className="max-w-4xl bg-white"
          style={{ borderRadius: '24px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Truck className="h-6 w-6" style={{ color: enconOrange }} />
              <span>{viewingTrunking?.name || 'Trunking Transfer'}</span>
              {viewingTrunking?.created_at && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  {new Date(viewingTrunking.created_at).toLocaleDateString('en-GB')}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Photo bar at top of modal */}
          {viewingTrunking && (() => {
            const tid = viewingTrunking.id;
            const trunk = recentTrunkingTransfers.find(t => t.transfer_ids?.includes(tid));
            const hasPhotos = trunk?.total_photos > 0 || trunk?.preview_photos?.length > 0;
            return (
              <div className="flex items-center gap-2 px-1 flex-shrink-0">
                {(trunk?.preview_photos || []).map(photo => (
                  <div 
                    key={photo.id} 
                    className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 border border-gray-200 cursor-pointer hover:border-orange-400 transition-colors"
                    onClick={() => openPhotoModal(tid)}
                  >
                    <img src={getPhotoUrl(photo.id)} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
                <label className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer border border-dashed border-gray-300 hover:border-orange-400">
                  <Camera className="h-4 w-4" />
                  {hasPhotos ? 'More' : 'Add Photos'}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files?.length) {
                        handlePhotoUpload(tid, Array.from(e.target.files));
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
                {hasPhotos && (
                  <button
                    className="text-xs text-orange-600 hover:underline ml-1"
                    onClick={() => openPhotoModal(tid)}
                  >
                    View all ({trunk?.total_photos || 0})
                  </button>
                )}
              </div>
            );
          })()}

          {viewingTrunking && (
            <div className="flex-1 overflow-y-auto space-y-4 py-2">
              {/* Workflow at top */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  {(() => {
                    const now = new Date();
                    now.setHours(0, 0, 0, 0);
                    const legs = viewingTrunking.legs || [];
                    const allDates = legs.map(l => l.scheduled_date).filter(Boolean);
                    const allDatesPassed = allDates.length > 0 && allDates.every(d => {
                      const dObj = new Date(d);
                      dObj.setHours(0, 0, 0, 0);
                      return dObj < now;
                    });
                    
                    return legs.map((leg, idx) => {
                      // Color for the origin branch (only first leg)
                      const getColor = (date, isLast) => {
                        const dateObj = date ? new Date(date) : null;
                        if (dateObj) dateObj.setHours(0, 0, 0, 0);
                        const diffMs = dateObj ? dateObj.getTime() - now.getTime() : null;
                        const diffDays = diffMs !== null ? diffMs / (1000 * 60 * 60 * 24) : null;
                        
                        if (isLast) {
                          return allDatesPassed ? 'bg-purple-500 text-white' : 'bg-gray-400 text-white';
                        }
                        if (diffDays !== null && diffDays < 0) return 'bg-purple-500 text-white';
                        if (diffDays !== null && diffDays <= 1) return 'bg-yellow-400 text-yellow-900';
                        return 'bg-green-500 text-white';
                      };
                      
                      const isLastLeg = idx === legs.length - 1;
                      
                      return (
                        <div key={idx} className="flex items-center gap-2">
                          {idx === 0 && (
                            <div className={`px-3 py-2 rounded-lg text-sm font-semibold ${getColor(leg.scheduled_date, false)}`}>
                              {leg.from_branch_name.replace('Encon ', '').replace('Nevill Long ', '')}
                            </div>
                          )}
                          <div className="flex flex-col items-center">
                            <ArrowRight className="h-5 w-5 text-gray-400" />
                            <span className="text-[10px] text-gray-500">{formatDate(leg.scheduled_date)}</span>
                          </div>
                          <div className={`px-3 py-2 rounded-lg text-sm font-semibold ${getColor(leg.scheduled_date, isLastLeg)}`}>
                            {leg.to_branch_name.replace('Encon ', '').replace('Nevill Long ', '')}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Leg Details */}
              {viewingTrunking.legs?.map((leg, idx) => {
                // Try multiple ways to find the branch address
                const fromAddr = branchAddresses[leg.from_branch_id] || 
                                 branchAddresses[leg.from_branch_name] || 
                                 branchAddresses[leg.from_branch_name?.toLowerCase()] ||
                                 branchAddresses[leg.from_branch_id?.toLowerCase()] || {};
                const toAddr = branchAddresses[leg.to_branch_id] || 
                               branchAddresses[leg.to_branch_name] || 
                               branchAddresses[leg.to_branch_name?.toLowerCase()] ||
                               branchAddresses[leg.to_branch_id?.toLowerCase()] || {};
                
                // Debug logging
                console.log('Branch lookup debug:', {
                  from_branch_id: leg.from_branch_id,
                  from_branch_name: leg.from_branch_name,
                  to_branch_id: leg.to_branch_id,
                  to_branch_name: leg.to_branch_name,
                  fromAddrFound: !!fromAddr.address1,
                  toAddrFound: !!toAddr.address1,
                  branchAddressKeys: Object.keys(branchAddresses).slice(0, 10)
                });
                
                return (
                <Card key={idx} className="border-2 border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-bold ${
                          leg.status === 'completed' ? 'bg-green-500 text-white' :
                          leg.status === 'in_transit' ? 'bg-yellow-500 text-white' :
                          'bg-blue-500 text-white'
                        }`}>
                          {leg.status === 'completed' ? <CheckCircle2 className="h-4 w-4" /> : leg.leg_number}
                        </span>
                        <span className="font-semibold">
                          Leg {leg.leg_number}: {leg.from_branch_name} → {leg.to_branch_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          leg.status === 'completed' ? 'bg-green-100 text-green-700' :
                          leg.status === 'in_transit' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {leg.status || 'pending'}
                        </span>
                      </div>
                    </div>

                    {/* Branch Addresses */}
                    <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div>
                        <p className="text-xs font-semibold text-purple-700 mb-1">FROM: {leg.from_branch_name}</p>
                        <div className="text-xs text-gray-600">
                          {fromAddr.address1 && <p>{fromAddr.address1}</p>}
                          {fromAddr.address2 && <p>{fromAddr.address2}</p>}
                          {(fromAddr.city || fromAddr.postcode) && (
                            <p>{[fromAddr.city, fromAddr.postcode].filter(Boolean).join(', ')}</p>
                          )}
                          {fromAddr.phone && <p>Tel: {fromAddr.phone}</p>}
                          {!fromAddr.address1 && !fromAddr.city && <p className="italic text-gray-400">No address on file</p>}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-purple-700 mb-1">TO: {leg.to_branch_name}</p>
                        <div className="text-xs text-gray-600">
                          {toAddr.address1 && <p>{toAddr.address1}</p>}
                          {toAddr.address2 && <p>{toAddr.address2}</p>}
                          {(toAddr.city || toAddr.postcode) && (
                            <p>{[toAddr.city, toAddr.postcode].filter(Boolean).join(', ')}</p>
                          )}
                          {toAddr.phone && <p>Tel: {toAddr.phone}</p>}
                          {!toAddr.address1 && !toAddr.city && <p className="italic text-gray-400">No address on file</p>}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Action:</span>
                        <p className="font-medium">
                          {leg.action_type === 'collect' 
                            ? `${leg.to_branch_name.split(' ')[1]} collects` 
                            : `${leg.from_branch_name.split(' ')[1]} delivers`}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Sent By:</span>
                        <p className="font-medium">{leg.action_by || 'TBD'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Date:</span>
                        <p className="font-medium">{formatDate(leg.scheduled_date)}</p>
                      </div>
                    </div>

                    {leg.cumulative_items && leg.cumulative_items.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-gray-500 mb-2">Products on this leg:</p>
                        <div className="space-y-1">
                          {leg.cumulative_items.map((item, i) => (
                            <div key={i} className="flex items-center justify-between text-sm bg-gray-50 px-2 py-1 rounded">
                              <span>{item.product_code} - {item.description}</span>
                              <span className="font-medium">×{item.quantity}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
              })}

              {viewingTrunking.notes && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Notes:</p>
                  <p className="text-sm">{viewingTrunking.notes}</p>
                </div>
              )}

              {/* Action Buttons - Same as normal transfer */}
              <div className="flex flex-wrap gap-3 pt-4 sticky bottom-0 bg-white pb-2">
                <Button
                  variant="outline"
                  className="flex-1 min-w-[100px] py-4 rounded-xl border-2"
                  style={{ borderColor: enconOrange, color: enconOrange, backgroundColor: 'white' }}
                  onClick={handleEditTrunking}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  className="py-4 px-4 rounded-xl border-2"
                  style={{ borderColor: enconOrange, color: enconOrange, backgroundColor: 'white' }}
                  onClick={handleViewTrunkingPdf}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View
                </Button>
                <Button
                  variant="outline"
                  className="py-4 px-4 rounded-xl border-2 border-red-400 text-red-600 hover:bg-red-50"
                  onClick={handleDeleteTrunking}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="py-4 px-4 rounded-xl border-2"
                  style={{ borderColor: enconOrange, color: enconOrange, backgroundColor: 'white' }}
                  onClick={handlePrintTrunking}
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
                <Button
                  className="flex-1 min-w-[140px] py-4 rounded-xl text-white font-semibold"
                  style={{ background: 'linear-gradient(135deg, #ee7203, #ec4899)' }}
                  onClick={handleSendTrunking}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send Transfer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Trunking PDF Preview Modal */}
      <Dialog open={showTrunkingPdfPreview} onOpenChange={setShowTrunkingPdfPreview}>
        <DialogContent 
          className="max-w-4xl bg-white"
          style={{ borderRadius: '24px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="h-6 w-6" style={{ color: enconOrange }} />
                <span>PDF Preview - {viewingTrunking?.name}</span>
              </div>
              <Button
                onClick={handleDownloadTrunkingPdf}
                className="text-white font-semibold rounded-xl"
                style={{ backgroundColor: enconOrange }}
              >
                <Printer className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto border rounded-xl p-4 bg-gray-50">
            <div 
              className="bg-white shadow-lg rounded-lg p-6"
              dangerouslySetInnerHTML={{ __html: trunkingPdfHtml }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Gallery Modal */}
      <Dialog open={!!photoModalTransferId && !fullSizePhoto} onOpenChange={(open) => { if (!open && !fullSizePhoto) { setPhotoModalTransferId(null); setPhotoModalPhotos([]); } }}>
        <DialogContent className="max-w-3xl bg-white" style={{ borderRadius: '24px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Camera className="h-5 w-5" style={{ color: enconOrange }} />
              Transfer Photos
              <span className="text-sm font-normal text-gray-500">({photoModalPhotos.length} photo{photoModalPhotos.length !== 1 ? 's' : ''})</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {photoModalLoading ? (
              <div className="text-center py-12 text-gray-500">Loading photos...</div>
            ) : photoModalPhotos.length === 0 ? (
              <div className="text-center py-12">
                <Image className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No photos yet</p>
                <Button
                  variant="outline"
                  className="border-2 rounded-xl"
                  style={{ borderColor: enconOrange, color: enconOrange }}
                  onClick={() => photoModalFileRef.current?.click()}
                  data-testid="photo-modal-upload-empty"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Add Photos
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {photoModalPhotos.map((photo) => (
                  <div key={photo.id} className="relative group aspect-square rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-100 cursor-pointer hover:border-orange-400 transition-colors" data-testid={`photo-modal-item-${photo.id}`} onClick={() => setFullSizePhoto(photo)}>
                    <img 
                      src={getPhotoUrl(photo.id)} 
                      alt={photo.original_filename} 
                      className="w-full h-full object-cover"
                    />
                    {/* Delete button on hover */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        className="p-1.5 bg-red-500/90 rounded-lg hover:bg-red-600 transition-colors"
                        onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photoModalTransferId, photo.id); }}
                        data-testid={`photo-delete-${photo.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add more photos button */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-200 flex-shrink-0">
            <p className="text-xs text-gray-400">
              {photoUploading ? 'Uploading...' : 'Click a photo to view full size'}
            </p>
            <Button
              variant="outline"
              className="flex items-center gap-2 rounded-xl border-2"
              style={{ borderColor: enconOrange, color: enconOrange }}
              onClick={() => photoModalFileRef.current?.click()}
              disabled={photoUploading}
              data-testid="photo-modal-add-more"
            >
              {photoUploading ? (
                <div className="h-4 w-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Add Photos
            </Button>
          </div>
          <input
            ref={photoModalFileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length && photoModalTransferId) {
                handlePhotoUpload(photoModalTransferId, Array.from(e.target.files));
                e.target.value = '';
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Full-Size Photo Viewer */}
      <Dialog open={!!fullSizePhoto} onOpenChange={(open) => { if (!open) { setFullSizePhoto(null); } }}>
        <DialogContent className="max-w-5xl bg-black/95 border-0 p-0" style={{ borderRadius: '16px', maxHeight: '90vh' }} onPointerDownOutside={(e) => e.preventDefault()}>
          <div className="relative flex flex-col h-full">
            {/* Top bar */}
            <div className="flex items-center justify-between p-3 bg-black/80">
              <span className="text-white text-sm truncate max-w-md">{fullSizePhoto?.original_filename}</span>
              <div className="flex items-center gap-2">
                <button 
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                  onClick={() => { handleDeletePhoto(photoModalTransferId, fullSizePhoto.id); }}
                  data-testid="fullsize-delete"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
                <button 
                  className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                  onClick={() => setFullSizePhoto(null)}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            {/* Image */}
            <div className="flex-1 flex items-center justify-center p-4" style={{ minHeight: '60vh' }}>
              {fullSizePhoto && (
                <img 
                  src={getPhotoUrl(fullSizePhoto.id)} 
                  alt={fullSizePhoto.original_filename}
                  className="rounded-lg"
                  style={{ maxWidth: '100%', maxHeight: '75vh', width: 'auto', height: 'auto', minWidth: '40vw', minHeight: '40vh', objectFit: 'contain' }}
                  data-testid="fullsize-image"
                />
              )}
            </div>
            {/* Navigation between photos */}
            {photoModalPhotos.length > 1 && (() => {
              const currentIdx = photoModalPhotos.findIndex(p => p.id === fullSizePhoto?.id);
              return (
                <div className="flex items-center justify-center gap-4 pb-4">
                  <button
                    className="p-2 text-white hover:bg-white/10 rounded-full disabled:opacity-30"
                    disabled={currentIdx <= 0}
                    onClick={() => setFullSizePhoto(photoModalPhotos[currentIdx - 1])}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <span className="text-white text-sm">{currentIdx + 1} / {photoModalPhotos.length}</span>
                  <button
                    className="p-2 text-white hover:bg-white/10 rounded-full disabled:opacity-30"
                    disabled={currentIdx >= photoModalPhotos.length - 1}
                    onClick={() => setFullSizePhoto(photoModalPhotos[currentIdx + 1])}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockTransfer;
